// Generated from aws-clf-c02-complete-guide.html (content kept verbatim)
export interface GuideSection { id: string; icon: string; title: string; subtitle: string; html: string; }
export interface GuidePart { id: string; week: string; title: string; desc: string; sections: GuideSection[]; }
export interface PracticeTopic { t: string; i: string; s: string; q: { q: string; o: string[]; a: number; e: string; r: string; w: string[] }[]; }
export const guideParts: GuidePart[] = [
 {
  "id": "part1",
  "week": "Week 1",
  "title": "Cloud Concepts & Global Infrastructure",
  "desc": "What the cloud is, service and deployment models, the six advantages, Well-Architected, Regions and AZs.",
  "sections": [
   {
    "id": "t1",
    "icon": "☁️",
    "title": "1.1 What is cloud computing?",
    "subtitle": "The foundation for everything else",
    "html": "<p class=\"lead\"><b>Cloud computing</b> is the on-demand delivery of IT resources (compute, storage, databases, networking, analytics and more) over the internet with pay-as-you-go pricing.</p>\n<div class=\"an\"><b>Analogy:</b> Think of electricity. You do not build a power plant to run your lights; you plug in and pay for the units you use. Cloud does the same for IT: you do not buy servers, you rent capacity.</div>\n<h3>The problem it solves</h3>\n<p>Traditionally, a company had to forecast demand, buy servers months ahead, find room in a data center, hire staff to maintain them, and hope the forecast was right. Buy too little and customers face outages; buy too much and money sits idle. Cloud removes the guesswork because capacity is available in minutes and can be released when no longer needed.</p>\n<h3>Five defining characteristics</h3>\n<ul><li><b>On-demand self-service:</b> you create resources yourself, through the console, CLI or API, without calling a vendor.</li><li><b>Broad network access:</b> services are reachable over the internet from laptops, phones and other servers.</li><li><b>Resource pooling:</b> AWS serves many customers from shared infrastructure, with strong logical isolation between them.</li><li><b>Rapid elasticity:</b> capacity scales out and in quickly, sometimes automatically.</li><li><b>Measured service:</b> usage is metered, so you are billed for what you consume.</li></ul>\n<h3>Three ways to use AWS</h3>\n<p>The <b>AWS Management Console</b> (web interface), the <b>AWS CLI</b> (commands in a terminal), and <b>SDKs/APIs</b> (from application code). All three do the same work; automation uses the CLI or SDKs.</p>\n<div class=\"tip\"><b>Exam tip:</b> Keywords like \"on-demand\", \"pay-as-you-go\" and \"scale quickly\" all point to the cloud value proposition.</div>"
   },
   {
    "id": "t2",
    "icon": "🧩",
    "title": "1.2 Cloud service models",
    "subtitle": "IaaS, PaaS and SaaS",
    "html": "<p>The models describe how much of the technology stack you manage versus how much the provider manages. Moving right in the diagram, AWS takes on more work and you gain convenience, but lose some control.</p>\n<figure class=\"fig\"><img class=\"aws-img\" src=\"/images/aws-clf/aws-clf-01-who-manages-what-in-on-premises-iaas-paas-and-saas.svg\" alt=\"Who manages what in on-premises, IaaS, PaaS and SaaS\" width=\"624\" height=\"342\" loading=\"lazy\" style=\"min-width:624px;max-width:624px\"><figcaption>Blue = you manage it · Grey = the provider manages it</figcaption></figure>\n<table><tr><th>Model</th><th>You get</th><th>You manage</th><th>AWS example</th></tr>\n<tr><td>IaaS</td><td>Virtual servers, storage, networks</td><td>OS, runtime, app, data</td><td>Amazon EC2, EBS, VPC</td></tr>\n<tr><td>PaaS</td><td>A ready platform to deploy code</td><td>Your application and data</td><td>Elastic Beanstalk, RDS</td></tr>\n<tr><td>SaaS</td><td>A finished application</td><td>Only your usage and settings</td><td>Amazon WorkMail, Chime</td></tr></table>\n<div class=\"an\"><b>Analogy (pizza):</b> IaaS is buying ingredients and cooking at home. PaaS is a take-and-bake pizza. SaaS is dining at a restaurant: you only eat.</div>\n<div class=\"trap\"><b>Trap:</b> Do not assume \"serverless\" means SaaS. Lambda is a compute service where you still write the code; AWS only removes server management.</div>"
   },
   {
    "id": "t3",
    "icon": "🏢",
    "title": "1.3 Cloud deployment models",
    "subtitle": "Where the infrastructure lives",
    "html": "<table><tr><th>Model</th><th>What it means</th><th>Typical reason to choose it</th></tr>\n<tr><td>Cloud (public)</td><td>Everything runs on a provider like AWS. No on-premises hardware.</td><td>Speed, scale and low upfront cost, such as startups and new apps</td></tr>\n<tr><td>Hybrid</td><td>On-premises systems connected to AWS, often by VPN or Direct Connect.</td><td>Legacy systems, data-residency rules, gradual migration</td></tr>\n<tr><td>On-premises (private cloud)</td><td>Resources in your own data center, using virtualization and management tools.</td><td>Strict control or regulatory needs. You still own the hardware cost</td></tr></table>\n<p><b>AWS Outposts</b> extends this idea: AWS installs its hardware and services in your own data center, which is a common hybrid building block.</p>\n<div class=\"tip\"><b>Exam tip:</b> \"Some workloads must stay on-site, others move to AWS\" always means hybrid.</div>"
   },
   {
    "id": "t4",
    "icon": "⭐",
    "title": "1.4 The six advantages of cloud computing",
    "subtitle": "A frequently tested list",
    "html": "<table><tr><th>Advantage</th><th>What it means in practice</th></tr>\n<tr><td>1. Trade fixed expense for variable expense</td><td>Instead of investing heavily in data centers up front (CapEx), you pay only as you consume (OpEx).</td></tr>\n<tr><td>2. Benefit from massive economies of scale</td><td>AWS aggregates usage from hundreds of thousands of customers, so it buys and operates at lower cost and passes savings on.</td></tr>\n<tr><td>3. Stop guessing capacity</td><td>Scale up or down as demand changes instead of predicting it and ending up with idle or insufficient capacity.</td></tr>\n<tr><td>4. Increase speed and agility</td><td>New resources take minutes, so experiments are cheap and teams ship faster.</td></tr>\n<tr><td>5. Stop spending money running data centers</td><td>Focus on customers and products, not racking servers or paying for power and cooling.</td></tr>\n<tr><td>6. Go global in minutes</td><td>Deploy to Regions worldwide to give users lower latency.</td></tr></table>\n<div class=\"tip\"><b>Exam tip:</b> Match the keyword: \"no upfront investment\" = variable expense; \"lower per-unit price\" = economies of scale; \"deploy in another country quickly\" = go global.</div>"
   },
   {
    "id": "t5",
    "icon": "📈",
    "title": "1.5 Cloud economics &amp; elasticity",
    "subtitle": "CapEx vs OpEx, TCO and scaling",
    "html": "<p><b>Capital expenditure (CapEx)</b> is money spent upfront on assets such as servers. <b>Operational expenditure (OpEx)</b> is ongoing pay-for-use spending. Cloud shifts spending from CapEx to OpEx, improving cash flow and lowering risk.</p>\n<figure class=\"fig\"><img class=\"aws-img\" src=\"/images/aws-clf/aws-clf-02-fixed-capacity-versus-cloud-capacity-that-follows-demand-over-ti.svg\" alt=\"Fixed capacity versus cloud capacity that follows demand over time\" width=\"640\" height=\"262\" loading=\"lazy\" style=\"min-width:640px;max-width:640px\"><figcaption>Traditional capacity must cover the peak, so most of the time you pay for idle servers</figcaption></figure>\n<h3>Elasticity vs scalability</h3>\n<ul><li><b>Scalability:</b> the ability to handle growth, either by a bigger machine (vertical) or more machines (horizontal).</li><li><b>Elasticity:</b> scaling out <i>and back in</i> automatically as demand rises and falls, so you stop paying when demand drops.</li></ul>\n<h3>Total Cost of Ownership (TCO)</h3>\n<p>On-premises costs go well beyond the server price: space, power, cooling, networking, security, maintenance staff, refresh cycles and idle capacity. The <b>AWS Pricing Calculator</b> lets you estimate AWS costs and compare against an on-premises estimate, so you judge the full picture instead of the hardware price alone.</p>\n<div class=\"trap\"><b>Trap:</b> Cloud is not always cheaper for every steady, fully utilized workload. Its strengths are flexibility, speed and avoiding waste, and savings improve with right-sizing and commitment discounts.</div>"
   },
   {
    "id": "t6",
    "icon": "🏛️",
    "title": "1.6 AWS Well-Architected Framework",
    "subtitle": "Six pillars for good design",
    "html": "<p>A set of best practices AWS uses to help you review and improve workloads. The <b>AWS Well-Architected Tool</b> in the console guides you through the review.</p>\n<table><tr><th>Pillar</th><th>Focus</th><th>Key design ideas</th></tr>\n<tr><td>Operational Excellence</td><td>Running and monitoring systems, improving processes</td><td>Automate with code, make small reversible changes, learn from failures</td></tr>\n<tr><td>Security</td><td>Protecting data, systems and assets</td><td>Strong identity, traceability, encrypt data, apply security at all layers</td></tr>\n<tr><td>Reliability</td><td>Recovering from failure and meeting demand</td><td>Test recovery, scale horizontally, automate recovery, avoid single points of failure</td></tr>\n<tr><td>Performance Efficiency</td><td>Using resources efficiently as needs change</td><td>Use managed services and serverless, go global, experiment often</td></tr>\n<tr><td>Cost Optimization</td><td>Avoiding unnecessary cost</td><td>Pay only for what you use, measure spend, right-size, adopt consumption models</td></tr>\n<tr><td>Sustainability</td><td>Reducing environmental impact</td><td>Maximize utilization, use efficient hardware and managed services</td></tr></table>\n<div class=\"an\"><b>Memory trick:</b> \"Our Security Relies on People, Costs and Sustainability\" gives O-S-R-P-C-S, the pillars in the order above.</div>"
   },
   {
    "id": "t7",
    "icon": "🌍",
    "title": "1.7 AWS global infrastructure",
    "subtitle": "Regions, AZs and edge locations",
    "html": "<figure class=\"fig\"><img class=\"aws-img\" src=\"/images/aws-clf/aws-clf-03-region-with-three-availability-zones-and-edge-locations-serving-.svg\" alt=\"Region with three availability zones and edge locations serving users\" width=\"564\" height=\"350\" loading=\"lazy\" style=\"min-width:564px;max-width:564px\"></figure>\n<h3>Region</h3><p>A physical geographic area (such as Mumbai or N. Virginia) containing multiple, isolated AZs. Data stays in the Region you choose unless you move it, which matters for compliance.</p>\n<h3>Availability Zone (AZ)</h3><p>One or more discrete data centers with redundant power, networking and connectivity, located far enough apart to avoid shared failures yet close enough for fast replication. Every Region has at least three.</p>\n<h3>Edge locations</h3><p>Sites used by CloudFront and Route 53 to cache content and answer DNS close to users. There are many more edge locations than Regions. <b>Local Zones</b> and <b>Wavelength</b> bring compute even closer to cities and 5G networks for ultra-low latency.</p>\n<h3>How to choose a Region</h3>\n<ol><li><b>Compliance and data residency:</b> laws may require data to stay in a country.</li><li><b>Latency:</b> pick the Region closest to most users.</li><li><b>Service availability:</b> newer services launch in some Regions first.</li><li><b>Price:</b> costs vary between Regions.</li></ol>\n<h3>High availability vs fault tolerance</h3>\n<p><b>High availability</b> means the system recovers quickly from failures, such as running across two AZs behind a load balancer. <b>Fault tolerance</b> means it keeps working with no interruption even when a component fails, which usually costs more.</p>\n<table><tr><th>Scope</th><th>Examples</th></tr><tr><td>Global</td><td>IAM, Route 53, CloudFront, AWS Organizations</td></tr><tr><td>Regional</td><td>EC2, VPC, Lambda, RDS, DynamoDB, S3 bucket data</td></tr><tr><td>AZ-bound</td><td>An EC2 instance, an EBS volume, a subnet</td></tr></table>\n<div class=\"trap\"><b>Trap:</b> S3 bucket <i>names</i> are globally unique, but a bucket's data lives in one Region. EBS volumes live in one AZ only.</div>"
   }
  ]
 },
 {
  "id": "part2",
  "week": "Week 2",
  "title": "Core AWS Services",
  "desc": "Compute, storage, databases and networking, with the architecture and VPC diagrams.",
  "sections": [
   {
    "id": "c1",
    "icon": "🖥️",
    "title": "2.1 Amazon EC2 (virtual servers)",
    "subtitle": "Infrastructure as a Service",
    "html": "<p class=\"lead\"><b>Amazon Elastic Compute Cloud (EC2)</b> gives you resizable virtual machines, called <b>instances</b>, in minutes. You choose the operating system, size, storage and network, and you are responsible for patching the OS and your software.</p>\n<div class=\"an\"><b>Analogy:</b> EC2 is like renting a computer in a giant warehouse. You pick the model and install what you want, and the landlord looks after the building, power and hardware.</div>\n<h3>Key building blocks</h3>\n<ul><li><b>AMI (Amazon Machine Image):</b> a template containing the OS and software used to launch instances. Launch many identical servers from one AMI.</li><li><b>Instance type:</b> the CPU, memory, storage and network profile, such as <code>t3.micro</code>. The letter shows the family, the number the generation, and the word the size.</li><li><b>Security group:</b> a virtual firewall around the instance that allows traffic in and out.</li><li><b>Key pair:</b> credentials used to log in securely to Linux (SSH) or retrieve the Windows password.</li><li><b>User data:</b> a script that runs on first boot to install software automatically.</li></ul>\n<h3>Instance families</h3>\n<table><tr><th>Family</th><th>Strength</th><th>Use case</th></tr><tr><td>General purpose (T, M)</td><td>Balanced CPU and memory</td><td>Web servers, small databases</td></tr><tr><td>Compute optimized (C)</td><td>High-performance CPUs</td><td>Batch jobs, gaming, media encoding</td></tr><tr><td>Memory optimized (R, X)</td><td>Large memory</td><td>In-memory databases, big analytics</td></tr><tr><td>Storage optimized (I, D)</td><td>Fast local disk throughput</td><td>Data warehouses, NoSQL</td></tr><tr><td>Accelerated computing (P, G)</td><td>GPUs</td><td>Machine learning, graphics</td></tr></table>\n<h3>How you pay</h3>\n<p>On-Demand, Savings Plans, Reserved, Spot and Dedicated Hosts are covered in the billing part. For now remember: steady usage is cheaper with commitments, and interruptible jobs are cheapest on Spot.</p>\n<div class=\"trap\"><b>Trap:</b> Stopping an instance stops compute charges, but attached EBS volumes still cost money. Terminating deletes the instance for good.</div>"
   },
   {
    "id": "c2",
    "icon": "⚡",
    "title": "2.2 Serverless and container compute",
    "subtitle": "When you do not want to manage servers",
    "html": "<h3>AWS Lambda</h3>\n<p>Upload code and Lambda runs it in response to events (a file landing in S3, an API call, a schedule). There are no servers to manage, it scales automatically, and you pay only for the milliseconds your code runs. Each run is limited to <b>15 minutes</b>.</p>\n<h3>Containers</h3>\n<p>A <b>container</b> packages an app with everything it needs, so it runs the same everywhere.</p>\n<table><tr><th>Service</th><th>What it is</th><th>Choose it when</th></tr><tr><td>Amazon ECS</td><td>AWS-native container orchestrator</td><td>Simple way to run Docker containers</td></tr><tr><td>Amazon EKS</td><td>Managed Kubernetes</td><td>You already use Kubernetes or want portability</td></tr><tr><td>AWS Fargate</td><td>Serverless engine for ECS and EKS</td><td>Run containers without managing servers</td></tr><tr><td>Amazon ECR</td><td>Container image registry</td><td>Store and share container images</td></tr></table>\n<h3>Other compute services</h3>\n<table><tr><th>Service</th><th>What it does</th></tr><tr><td>Elastic Beanstalk</td><td>Upload code; AWS handles deployment, scaling and load balancing (PaaS)</td></tr><tr><td>Amazon Lightsail</td><td>Simple, low-cost servers and websites with predictable monthly pricing</td></tr><tr><td>AWS Batch</td><td>Runs large numbers of batch jobs, choosing the right compute automatically</td></tr><tr><td>AWS Outposts</td><td>AWS infrastructure installed in your own data center</td></tr></table>\n<div class=\"tip\"><b>Exam tip:</b> \"Run code without provisioning servers\" = Lambda. \"Containers without managing servers\" = Fargate. \"Just deploy my web app\" = Elastic Beanstalk. \"Simple VPS for a small site\" = Lightsail.</div>"
   },
   {
    "id": "c3",
    "icon": "🗄️",
    "title": "2.3 Storage services",
    "subtitle": "Object, block and file storage",
    "html": "<h3>Three kinds of storage</h3>\n<table><tr><th>Type</th><th>Service</th><th>Think of it as</th></tr><tr><td>Object</td><td>Amazon S3</td><td>Files stored as objects with metadata, accessed over the internet via API</td></tr><tr><td>Block</td><td>Amazon EBS</td><td>A raw disk attached to one EC2 instance, like a hard drive</td></tr><tr><td>File</td><td>Amazon EFS, FSx</td><td>A shared file system that many servers mount at once</td></tr></table>\n<h3>Amazon S3 (Simple Storage Service)</h3>\n<p>Virtually unlimited object storage in containers called <b>buckets</b>. It is designed for <b>99.999999999% (11 nines) durability</b> by storing data redundantly across multiple AZs. Individual objects can be up to 5 TB. Use it for backups, static websites, data lakes, media and logs.</p>\n<ul><li><b>Security:</b> buckets are private by default. Control access with IAM policies, bucket policies, and Block Public Access. Encrypt at rest by default.</li><li><b>Versioning:</b> keeps old copies of objects to recover from accidental deletes.</li><li><b>Lifecycle rules:</b> automatically move or delete objects as they age.</li></ul>\n<h3>S3 storage classes</h3>\n<figure class=\"fig\"><img class=\"aws-img\" src=\"/images/aws-clf/aws-clf-04-s3-storage-classes-from-hot-to-cold-with-relative-storage-cost.svg\" alt=\"S3 storage classes from hot to cold with relative storage cost\" width=\"640\" height=\"412\" loading=\"lazy\" style=\"min-width:640px;max-width:640px\"><figcaption>Hot to cold: bar length shows relative storage price, shorter means cheaper per GB</figcaption></figure>\n<p>Colder classes cost less to store but charge more or take longer to retrieve. <b>Intelligent-Tiering</b> watches access and moves objects for you, ideal when the pattern is unknown. <b>One Zone-IA</b> keeps data in a single AZ, so it is cheaper but less resilient.</p>\n<h3>EBS, EFS, and others</h3>\n<ul><li><b>EBS:</b> persistent block storage for one instance, tied to <b>one AZ</b>. Back it up with snapshots (stored in S3), which can also restore into another AZ.</li><li><b>EFS:</b> an elastic, shared Linux file system that works across multiple AZs and grows automatically.</li><li><b>FSx:</b> managed specialized file systems such as Windows File Server and Lustre.</li><li><b>Storage Gateway:</b> connects on-premises systems to cloud storage (hybrid).</li><li><b>Snow Family:</b> rugged devices to move huge datasets physically when the network is too slow.</li><li><b>AWS Backup:</b> central policy-based backups across services.</li></ul>\n<div class=\"trap\"><b>Trap:</b> EBS is one AZ, EFS is multi-AZ. S3 is not a disk you attach to a server; use EBS for that.</div>"
   },
   {
    "id": "c4",
    "icon": "🧮",
    "title": "2.4 Database services",
    "subtitle": "Managed so you do not run database servers",
    "html": "<p>With a <b>managed database</b>, AWS handles hardware, patching, backups and failover. You focus on your data and queries.</p>\n<table><tr><th>Service</th><th>Type</th><th>Details</th></tr><tr><td>Amazon RDS</td><td>Relational (SQL)</td><td>MySQL, PostgreSQL, MariaDB, Oracle, SQL Server. Multi-AZ gives automatic failover. Read replicas scale reads.</td></tr><tr><td>Amazon Aurora</td><td>Relational (SQL)</td><td>AWS-built MySQL/PostgreSQL-compatible engine, up to 5x faster than MySQL, with storage copied six ways across 3 AZs.</td></tr><tr><td>Amazon DynamoDB</td><td>NoSQL key-value</td><td>Serverless, single-digit millisecond speed at any scale. Good for carts, sessions, gaming.</td></tr><tr><td>Amazon ElastiCache</td><td>In-memory cache</td><td>Redis or Memcached to speed up applications by caching frequent reads.</td></tr><tr><td>Amazon Redshift</td><td>Data warehouse</td><td>Analytics (OLAP) on huge datasets using SQL.</td></tr><tr><td>Amazon DocumentDB</td><td>Document database</td><td>MongoDB-compatible JSON documents.</td></tr><tr><td>Amazon Neptune</td><td>Graph database</td><td>Relationships such as social networks and fraud detection.</td></tr></table>\n<h3>Choosing quickly</h3>\n<ul><li>Need joins, transactions, existing SQL apps: <b>RDS or Aurora</b>.</li><li>Need massive scale with simple access patterns: <b>DynamoDB</b>.</li><li>Need faster reads for repeated queries: <b>ElastiCache</b>.</li><li>Need reports across years of data: <b>Redshift</b>.</li><li>Moving an existing database to AWS: <b>Database Migration Service (DMS)</b>.</li></ul>\n<div class=\"trap\"><b>Trap:</b> RDS Multi-AZ is for high availability (failover), while read replicas are for performance (more reads). They solve different problems.</div>"
   },
   {
    "id": "c5",
    "icon": "🌐",
    "title": "2.5 Networking &amp; content delivery",
    "subtitle": "Connecting and protecting your resources",
    "html": "<h3>Amazon VPC</h3>\n<p>A <b>Virtual Private Cloud</b> is your own isolated network in AWS. You choose its IP range and divide it into <b>subnets</b>, each inside one AZ. A <b>public subnet</b> has a route to the internet; a <b>private subnet</b> does not.</p>\n<figure class=\"fig\"><img class=\"aws-img\" src=\"/images/aws-clf/aws-clf-05-vpc-with-public-and-private-subnets-across-two-availability-zone.svg\" alt=\"VPC with public and private subnets across two Availability Zones\" width=\"811\" height=\"516\" loading=\"lazy\" style=\"min-width:640px;max-width:811px\"><figcaption>Typical design: internet-facing resources in public subnets, sensitive resources in private subnets, spread across two AZs</figcaption></figure>\n<ul><li><b>Internet gateway:</b> lets the VPC talk to the internet.</li><li><b>NAT gateway:</b> lets private resources reach out (for updates) without being reachable from outside.</li><li><b>Security groups:</b> stateful firewall at the <b>instance</b> level, allow rules only.</li><li><b>Network ACLs:</b> stateless firewall at the <b>subnet</b> level, allow and deny rules.</li><li><b>VPC peering and Transit Gateway:</b> connect VPCs; peering is not transitive.</li></ul>\n<h3>Reaching users and spreading traffic</h3>\n<figure class=\"fig\"><img class=\"aws-img\" src=\"/images/aws-clf/aws-clf-06-web-architecture-users-route-53-cloudfront-load-balancer-ec2-aut.svg\" alt=\"Web architecture: users, Route 53, CloudFront, load balancer, EC2 Auto Scaling, RDS database and S3 storage\" width=\"915\" height=\"274\" loading=\"lazy\" style=\"min-width:640px;max-width:915px\"><figcaption>A request flows left to right: DNS, cache, load balancing, servers, then data</figcaption></figure>\n<table><tr><th>Service</th><th>What it does</th></tr><tr><td>Elastic Load Balancing</td><td>Distributes traffic across healthy targets in multiple AZs (Application LB for HTTP, Network LB for extreme performance).</td></tr><tr><td>Auto Scaling</td><td>Adds or removes EC2 instances automatically to match demand and replace unhealthy ones.</td></tr><tr><td>Amazon Route 53</td><td>Scalable DNS plus routing policies (simple, weighted, latency, failover, geolocation) and health checks.</td></tr><tr><td>Amazon CloudFront</td><td>CDN that caches content at edge locations for low latency; also helps absorb attacks.</td></tr><tr><td>AWS Global Accelerator</td><td>Routes users over the AWS global network to the best endpoint using static IPs.</td></tr></table>\n<h3>Connecting to on-premises</h3>\n<table><tr><th>Option</th><th>How</th><th>Notes</th></tr><tr><td>Site-to-Site VPN</td><td>Encrypted tunnel over the internet</td><td>Fast to set up, variable performance</td></tr><tr><td>Direct Connect</td><td>Dedicated private fiber to AWS</td><td>Consistent speed and lower latency; not encrypted by default</td></tr><tr><td>Client VPN</td><td>Remote users connect securely</td><td>Employees working remotely</td></tr></table>\n<div class=\"trap\"><b>Trap:</b> Security groups are stateful (return traffic is allowed automatically) and NACLs are stateless. Direct Connect is private but not encrypted unless you add a VPN.</div>"
   },
   {
    "id": "c6",
    "icon": "🧭",
    "title": "2.6 Quick decision cheat sheet",
    "subtitle": "Scenario keyword to service",
    "html": "<table><tr><th>Requirement</th><th>Service</th></tr><tr><td>Virtual server with full control</td><td>EC2</td></tr><tr><td>Run short code on events, no servers</td><td>Lambda</td></tr><tr><td>Containers without managing servers</td><td>Fargate</td></tr><tr><td>Store files, backups, static website</td><td>S3</td></tr><tr><td>Disk for one EC2 instance</td><td>EBS</td></tr><tr><td>Shared file system across servers</td><td>EFS</td></tr><tr><td>Rarely accessed archives, lowest cost</td><td>S3 Glacier Deep Archive</td></tr><tr><td>Managed SQL database</td><td>RDS / Aurora</td></tr><tr><td>Serverless NoSQL at scale</td><td>DynamoDB</td></tr><tr><td>Cache to speed up reads</td><td>ElastiCache</td></tr><tr><td>Data warehouse and analytics</td><td>Redshift</td></tr><tr><td>Private network in AWS</td><td>VPC</td></tr><tr><td>Distribute traffic across servers</td><td>Elastic Load Balancing</td></tr><tr><td>DNS and domain routing</td><td>Route 53</td></tr><tr><td>Cache content near users</td><td>CloudFront</td></tr><tr><td>Dedicated private line to AWS</td><td>Direct Connect</td></tr></table>"
   }
  ]
 },
 {
  "id": "part3",
  "week": "Week 3",
  "title": "Security, Compliance & Monitoring",
  "desc": "Shared responsibility, IAM, protection services, governance, CloudWatch vs CloudTrail vs Config.",
  "sections": [
   {
    "id": "s1",
    "icon": "🤝",
    "title": "3.1 The shared responsibility model",
    "subtitle": "The most tested security concept",
    "html": "<p class=\"lead\">AWS is responsible for <b>security of the cloud</b>. You are responsible for <b>security in the cloud</b>.</p>\n<figure class=\"fig\"><img class=\"aws-img\" src=\"/images/aws-clf/aws-clf-07-shared-responsibility-model.svg\" alt=\"Shared responsibility model\" width=\"658\" height=\"336\" loading=\"lazy\" style=\"min-width:640px;max-width:658px\"><figcaption>The line between AWS and customer moves depending on the service</figcaption></figure>\n<div class=\"an\"><b>Analogy:</b> A landlord secures the building, the roof and the entrance. The tenant locks their own door, decides who gets a key, and protects their belongings.</div>\n<h3>The line moves with the service</h3>\n<table><tr><th>Service</th><th>AWS handles</th><th>You handle</th></tr><tr><td>EC2 (IaaS)</td><td>Hardware, host, data center</td><td>Guest OS patches, firewall rules, app, data, IAM</td></tr><tr><td>RDS (managed)</td><td>Hardware, OS and database patching, backups infrastructure</td><td>Data, access control, encryption choices, security groups</td></tr><tr><td>S3 (managed)</td><td>Storage infrastructure, durability, hardware</td><td>Who can access buckets, encryption settings, data classification</td></tr><tr><td>Lambda (serverless)</td><td>Servers, runtime, scaling</td><td>Your code, its permissions, the data it touches</td></tr></table>\n<h3>Three kinds of controls</h3>\n<ul><li><b>Inherited:</b> you rely fully on AWS, such as physical and environmental security.</li><li><b>Shared:</b> both sides act, such as patch management (AWS patches infrastructure, you patch your guest OS) and configuration management.</li><li><b>Customer-specific:</b> entirely yours, such as how you classify data and who may access applications.</li></ul>\n<div class=\"trap\"><b>Trap:</b> \"AWS is responsible for patching my EC2 operating system\" is false. AWS patches the host; you patch the guest OS.</div>"
   },
   {
    "id": "s2",
    "icon": "🔑",
    "title": "3.2 AWS Identity and Access Management (IAM)",
    "subtitle": "Who can do what",
    "html": "<p><b>IAM</b> controls authentication (who are you?) and authorization (what may you do?). It is <b>global</b> and free to use.</p>\n<figure class=\"fig\"><img class=\"aws-img\" src=\"/images/aws-clf/aws-clf-08-iam-users-groups-policies-roles-and-resources.svg\" alt=\"IAM users, groups, policies, roles and resources\" width=\"688\" height=\"338\" loading=\"lazy\" style=\"min-width:640px;max-width:688px\"><figcaption>Users get permissions through groups and policies; services use roles with temporary credentials</figcaption></figure>\n<table><tr><th>Concept</th><th>Meaning</th></tr><tr><td>Root user</td><td>The email-based account owner with unrestricted access. Protect it with MFA, do not use it daily, and do not create access keys for it.</td></tr><tr><td>IAM user</td><td>A person or application with long-term credentials and its own permissions.</td></tr><tr><td>IAM group</td><td>A collection of users that share the same permissions. Groups cannot contain other groups.</td></tr><tr><td>IAM role</td><td>An identity assumed temporarily by a service, user or another account. No permanent password or keys. Best practice for EC2 and Lambda.</td></tr><tr><td>IAM policy</td><td>A JSON document that allows or denies actions on resources.</td></tr></table>\n<h3>What a policy looks like</h3>\n<pre>{\n  \"Effect\": \"Allow\",\n  \"Action\": \"s3:GetObject\",\n  \"Resource\": \"arn:aws:s3:::my-bucket/*\"\n}</pre>\n<p>Read it as: <i>allow</i> the <i>GetObject</i> action on objects in <i>my-bucket</i>. Everything not explicitly allowed is denied by default, and an explicit deny always wins.</p>\n<h3>Best practices</h3>\n<ul><li><b>Least privilege:</b> grant only the permissions a task needs, then expand if required.</li><li><b>MFA</b> for the root user and all privileged users (virtual app, hardware key, or passkey).</li><li><b>Use roles</b> for applications instead of embedding access keys in code.</li><li><b>Rotate credentials</b> and enforce a strong password policy.</li><li><b>IAM Identity Center</b> gives employees single sign-on across accounts and applications.</li><li>Review access with the <b>credential report</b> and <b>IAM Access Analyzer</b>.</li></ul>\n<h3>Ways to access AWS</h3>\n<p>Console (password plus MFA), CLI and SDKs (access keys). Treat access keys like passwords: never commit them to code repositories.</p>\n<div class=\"tip\"><b>Exam tip:</b> \"Give an EC2 instance access to S3 securely\" = IAM role. \"Single sign-on for employees\" = IAM Identity Center. \"Extra login protection\" = MFA.</div>"
   },
   {
    "id": "s3",
    "icon": "🛡️",
    "title": "3.3 Security and protection services",
    "subtitle": "Which service defends against which threat",
    "html": "<h3>Encryption</h3>\n<ul><li><b>At rest:</b> data stored on disk or in a service, such as S3, EBS and RDS encryption.</li><li><b>In transit:</b> data moving across networks, protected by TLS/HTTPS.</li></ul>\n<table><tr><th>Service</th><th>Purpose</th></tr><tr><td>AWS KMS</td><td>Create and control encryption keys; integrates with most services and CloudTrail logs key use.</td></tr><tr><td>AWS CloudHSM</td><td>Dedicated hardware security module that you control, for strict compliance.</td></tr><tr><td>AWS Certificate Manager</td><td>Provision and renew SSL/TLS certificates.</td></tr><tr><td>AWS Secrets Manager</td><td>Store and automatically rotate database credentials and API keys.</td></tr></table>\n<h3>Threat detection and defense</h3>\n<table><tr><th>Service</th><th>What it does</th></tr><tr><td>AWS Shield</td><td>DDoS protection. Standard is free and automatic; Advanced adds extra protection, response support and cost protection.</td></tr><tr><td>AWS WAF</td><td>Web application firewall that blocks common web attacks such as SQL injection and cross-site scripting.</td></tr><tr><td>AWS Firewall Manager</td><td>Manage WAF and other firewall rules centrally across accounts.</td></tr><tr><td>Amazon GuardDuty</td><td>Intelligent threat detection by analyzing CloudTrail, VPC flow and DNS logs.</td></tr><tr><td>Amazon Inspector</td><td>Automated vulnerability scanning of EC2, containers and Lambda.</td></tr><tr><td>Amazon Macie</td><td>Uses machine learning to discover sensitive data such as personal information in S3.</td></tr><tr><td>Amazon Detective</td><td>Investigates the root cause of security findings.</td></tr><tr><td>AWS Security Hub</td><td>One dashboard of security findings and compliance checks across accounts.</td></tr><tr><td>Amazon Cognito</td><td>Sign-up and sign-in for your own web and mobile app users.</td></tr></table>\n<div class=\"tip\"><b>Exam tip:</b> DDoS = Shield. SQL injection = WAF. Sensitive data in S3 = Macie. Threat detection = GuardDuty. Vulnerabilities on servers = Inspector. Encryption keys = KMS.</div>"
   },
   {
    "id": "s4",
    "icon": "📜",
    "title": "3.4 Compliance and governance",
    "subtitle": "Proving and enforcing rules",
    "html": "<p>AWS supports many compliance programs, such as SOC, ISO 27001, PCI DSS and HIPAA eligibility. Being on AWS does not make you compliant automatically; you must configure your workloads correctly.</p>\n<table><tr><th>Service</th><th>Purpose</th></tr><tr><td>AWS Artifact</td><td>Self-service portal to download AWS audit reports and agreements. The place to find compliance documents.</td></tr><tr><td>AWS Organizations</td><td>Manage many accounts centrally, group them in organizational units, use consolidated billing, and apply Service Control Policies (SCPs).</td></tr><tr><td>Service Control Policies</td><td>Guardrails that set the maximum permissions for accounts. They limit, but do not grant, permissions.</td></tr><tr><td>AWS Control Tower</td><td>Sets up and governs a secure multi-account environment with best-practice guardrails.</td></tr><tr><td>AWS Audit Manager</td><td>Collects evidence continuously to simplify audits.</td></tr><tr><td>AWS Config</td><td>Records resource configurations and flags non-compliant changes.</td></tr></table>\n<div class=\"trap\"><b>Trap:</b> AWS Artifact provides compliance reports, not software artifacts or build outputs.</div>"
   },
   {
    "id": "s5",
    "icon": "📊",
    "title": "3.5 Monitoring, management and governance tools",
    "subtitle": "Watching, auditing and automating",
    "html": "<figure class=\"fig\"><img class=\"aws-img\" src=\"/images/aws-clf/aws-clf-09-cloudwatch-cloudtrail-and-config-each-answer-a-different-questio.svg\" alt=\"CloudWatch, CloudTrail and Config each answer a different question\" width=\"750\" height=\"235\" loading=\"lazy\" style=\"min-width:640px;max-width:750px\"><figcaption>Three services that sound alike but answer three different questions</figcaption></figure>\n<h3>Amazon CloudWatch</h3>\n<p>Collects <b>metrics</b> (CPU, network), <b>logs</b> and events from AWS and your applications. Create <b>alarms</b> that notify you or trigger actions (such as Auto Scaling) and build <b>dashboards</b>. Basic EC2 metrics arrive every 5 minutes; detailed monitoring is every 1 minute.</p>\n<h3>AWS CloudTrail</h3>\n<p>Records API activity in your account: who made a call, from where, and when. Use it for auditing, security analysis and troubleshooting. It is enabled by default for 90 days of event history; create a trail to keep longer.</p>\n<h3>More tools worth knowing</h3>\n<table><tr><th>Tool</th><th>What it does</th></tr><tr><td>Trusted Advisor</td><td>Checks your account against best practices for cost optimization, performance, security, fault tolerance and service limits. Full checks need Business or Enterprise support.</td></tr><tr><td>AWS Health Dashboard</td><td>Shows AWS service health and events that affect your resources.</td></tr><tr><td>Systems Manager</td><td>Operational hub to patch, configure and run commands across many instances without logging in.</td></tr><tr><td>CloudFormation</td><td>Infrastructure as Code: define resources in templates and create them repeatably.</td></tr><tr><td>AWS CDK</td><td>Define infrastructure using programming languages, which becomes CloudFormation.</td></tr><tr><td>AWS Config</td><td>Tracks configuration changes and checks them against rules.</td></tr><tr><td>AWS X-Ray</td><td>Traces requests through applications to find performance bottlenecks.</td></tr><tr><td>Service Catalog</td><td>Approved products that teams can deploy.</td></tr></table>\n<div class=\"tip\"><b>Exam tip:</b> \"Who deleted the bucket?\" = CloudTrail. \"CPU alarm\" = CloudWatch. \"Was this resource changed from the approved setup?\" = Config. \"Best-practice recommendations\" = Trusted Advisor.</div>"
   },
   {
    "id": "s6",
    "icon": "🧭",
    "title": "3.6 Quick decision cheat sheet",
    "subtitle": "Security keyword to service",
    "html": "<table><tr><th>Requirement</th><th>Service</th></tr><tr><td>Control who can access what</td><td>IAM</td></tr><tr><td>Temporary access for a service</td><td>IAM role</td></tr><tr><td>Single sign-on for staff</td><td>IAM Identity Center</td></tr><tr><td>Block DDoS attacks</td><td>Shield</td></tr><tr><td>Block SQL injection or XSS</td><td>WAF</td></tr><tr><td>Detect suspicious activity</td><td>GuardDuty</td></tr><tr><td>Scan servers for vulnerabilities</td><td>Inspector</td></tr><tr><td>Find personal data in S3</td><td>Macie</td></tr><tr><td>Manage encryption keys</td><td>KMS</td></tr><tr><td>Store and rotate secrets</td><td>Secrets Manager</td></tr><tr><td>Download compliance reports</td><td>Artifact</td></tr><tr><td>Manage multiple accounts</td><td>Organizations</td></tr><tr><td>Audit API calls</td><td>CloudTrail</td></tr><tr><td>Monitor performance, set alarms</td><td>CloudWatch</td></tr><tr><td>Check configuration compliance</td><td>Config</td></tr><tr><td>Best-practice recommendations</td><td>Trusted Advisor</td></tr></table>"
   }
  ]
 },
 {
  "id": "part4",
  "week": "Week 4",
  "title": "Billing, Support & Exam Strategy",
  "desc": "Pricing models, cost tools, support plans, integration, migration, AI/ML and the final-week plan.",
  "sections": [
   {
    "id": "b1",
    "icon": "💰",
    "title": "4.1 AWS pricing models",
    "subtitle": "Four principles and five ways to pay for compute",
    "html": "<h3>Four pricing principles</h3>\n<ul><li><b>Pay as you go:</b> no upfront cost or long-term contracts.</li><li><b>Pay less when you reserve:</b> commit to 1 or 3 years for a discount.</li><li><b>Pay less per unit when you use more:</b> tiered pricing (for example S3 gets cheaper per GB at higher volume).</li><li><b>Pay less as AWS grows:</b> economies of scale lead to periodic price reductions.</li></ul>\n<figure class=\"fig\"><img class=\"aws-img\" src=\"/images/aws-clf/aws-clf-10-maximum-savings-versus-on-demand-pricing.svg\" alt=\"Maximum savings versus On-Demand pricing\" width=\"700\" height=\"244\" loading=\"lazy\" style=\"min-width:640px;max-width:700px\"><figcaption>Longer shaded bar = bigger discount compared with On-Demand</figcaption></figure>\n<table><tr><th>Model</th><th>How it works</th><th>Best for</th></tr><tr><td>On-Demand</td><td>Pay by the second or hour. No commitment.</td><td>Short, unpredictable or first-time workloads</td></tr><tr><td>Savings Plans</td><td>Commit to an hourly spend for 1 or 3 years. Compute Savings Plans apply flexibly across EC2, Fargate and Lambda.</td><td>Steady usage where you want flexibility</td></tr><tr><td>Reserved Instances</td><td>Commit to a specific instance configuration for 1 or 3 years. Pay no, partial or all upfront; more upfront means a larger discount.</td><td>Steady-state, predictable workloads</td></tr><tr><td>Spot Instances</td><td>Use spare capacity at a steep discount, but AWS can reclaim it with short notice.</td><td>Fault-tolerant jobs: batch, big data, testing</td></tr><tr><td>Dedicated Hosts</td><td>A whole physical server for you. Allows bring-your-own server-bound licenses and meets strict compliance rules.</td><td>Licensing or regulatory requirements</td></tr></table>\n<h3>Data transfer pricing</h3>\n<ul><li>Data <b>into</b> AWS from the internet is free.</li><li>Data <b>out</b> to the internet is charged, with volume tiers.</li><li>Traffic between AZs or Regions has a cost; using private IPs inside the same AZ is cheapest.</li></ul>\n<div class=\"trap\"><b>Trap:</b> Standard Reserved Instances cannot be moved between instance families, whereas Convertible ones and Compute Savings Plans offer more flexibility. Spot is never right for work that cannot be interrupted.</div>"
   },
   {
    "id": "b2",
    "icon": "🧾",
    "title": "4.2 Billing and cost management tools",
    "subtitle": "Estimate, track, control and optimize",
    "html": "<table><tr><th>Tool</th><th>When</th><th>What it does</th></tr><tr><td>AWS Pricing Calculator</td><td>Before</td><td>Estimate the monthly cost of a planned architecture and compare scenarios.</td></tr><tr><td>AWS Budgets</td><td>During</td><td>Set cost or usage budgets and get alerts (email, SNS) when actual or forecast spend crosses a threshold.</td></tr><tr><td>AWS Cost Explorer</td><td>After</td><td>Visualize and analyze past spending, filter by service or tag, and forecast usage.</td></tr><tr><td>Cost and Usage Report (CUR)</td><td>After</td><td>The most detailed billing data, delivered to S3 for deeper analysis.</td></tr><tr><td>Cost Allocation Tags</td><td>Organize</td><td>Label resources (like Project=Web) to see cost by team or project.</td></tr><tr><td>Cost Anomaly Detection</td><td>Alert</td><td>Uses machine learning to flag unusual spending.</td></tr><tr><td>Compute Optimizer</td><td>Optimize</td><td>Recommends right-sized resources based on actual usage.</td></tr><tr><td>Billing alarms</td><td>Alert</td><td>CloudWatch alarm that notifies you when estimated charges exceed a limit.</td></tr></table>\n<h3>AWS Organizations and consolidated billing</h3>\n<p>Link many accounts to one management account to receive a <b>single bill</b>, combine usage to reach <b>volume discounts sooner</b>, and share Reserved Instance and Savings Plan benefits across accounts. There is no extra charge for the feature.</p>\n<h3>The AWS Free Tier</h3>\n<table><tr><th>Type</th><th>Duration</th><th>Examples</th></tr><tr><td>Always free</td><td>Never expires</td><td>Lambda monthly requests, DynamoDB storage allowance, and others</td></tr><tr><td>12 months free</td><td>From sign-up date for classic accounts</td><td>EC2 and S3 starter allowances</td></tr><tr><td>Short-term trials</td><td>Starts when you first use a service</td><td>Trial periods for selected services</td></tr></table>\n<p>Newer accounts may instead receive a credit-based free plan, so always read the current terms on the AWS Free Tier page and set a budget alert on day one.</p>"
   },
   {
    "id": "b3",
    "icon": "🛟",
    "title": "4.3 AWS Support plans",
    "subtitle": "Plans were restructured, so know both versions",
    "html": "<p>AWS has moved to a new set of support plans. The older Developer, Business and Enterprise On-Ramp plans stop on <b>January 1, 2027</b>, but existing study material and exam questions may still reference them, so learn both.</p>\n<h3>Current plans</h3>\n<table><tr><th>Plan</th><th>Price</th><th>Highlights</th></tr><tr><td>Basic</td><td>Free</td><td>Included with every account. Documentation, forums, Health Dashboard, billing and account support. No technical case support.</td></tr><tr><td>Business Support+</td><td>From $29/month (or a percentage of spend, whichever is higher)</td><td>Entry paid plan for production workloads: 24/7 technical support with AI-assisted help and all Trusted Advisor checks.</td></tr><tr><td>Enterprise Support</td><td>From $5,000/month</td><td>Adds a Technical Account Manager (TAM), architectural and Well-Architected reviews, and the fastest response times for critical issues.</td></tr><tr><td>Unified Operations</td><td>From $50,000/month</td><td>For large enterprises needing managed operational oversight and a dedicated team.</td></tr></table>\n<h3>Legacy plans (being retired)</h3>\n<table><tr><th>Legacy plan</th><th>Highlights</th></tr><tr><td>Developer</td><td>Business-hours email support; for testing and development</td></tr><tr><td>Business</td><td>24/7 phone, chat and email; full Trusted Advisor; production workloads</td></tr><tr><td>Enterprise On-Ramp</td><td>A pool of Technical Account Managers; business-critical workloads</td></tr><tr><td>Enterprise</td><td>Designated TAM and concierge team; mission-critical workloads</td></tr></table>\n<div class=\"tip\"><b>Exam tip:</b> Remember the pattern: free = Basic; production, 24/7 support = Business tier; a named Technical Account Manager = Enterprise tier. Check the latest AWS Support page before your exam date, because questions follow the current exam guide.</div>\n<h3>Other places to get help</h3>\n<ul><li><b>AWS Professional Services:</b> AWS experts who help with projects and migrations.</li><li><b>AWS Partner Network and Marketplace:</b> consulting partners and a catalog of third-party software.</li><li><b>AWS re:Post and documentation:</b> community answers and guides.</li><li><b>Trusted Advisor:</b> automated best-practice checks across cost, performance, security, fault tolerance and quotas.</li></ul>"
   },
   {
    "id": "b4",
    "icon": "🔗",
    "title": "4.4 Application integration and migration",
    "subtitle": "Connecting services and moving to AWS",
    "html": "<h3>Decoupling applications</h3>\n<p>In <b>loosely coupled</b> designs, parts of an application communicate through messages instead of calling each other directly, so a failure or traffic spike in one part does not break the rest.</p>\n<figure class=\"fig\"><img class=\"aws-img\" src=\"/images/aws-clf/aws-clf-11-sqs-queue-decouples-producer-and-consumer-sns-fans-out-to-many-s.svg\" alt=\"SQS queue decouples producer and consumer; SNS fans out to many subscribers\" width=\"452\" height=\"502\" loading=\"lazy\" style=\"min-width:452px;max-width:452px\"><figcaption>SQS: one consumer pulls from a queue · SNS: one message is pushed to many subscribers</figcaption></figure>\n<table><tr><th>Service</th><th>What it does</th></tr><tr><td>Amazon SQS</td><td>Message queue. Producers send messages; consumers poll and process them. Buffers spikes.</td></tr><tr><td>Amazon SNS</td><td>Publish/subscribe notifications pushed to email, SMS, Lambda, or SQS queues.</td></tr><tr><td>Amazon EventBridge</td><td>Event bus that routes events from AWS services, SaaS apps and your code to targets.</td></tr><tr><td>AWS Step Functions</td><td>Visual workflows that orchestrate multiple Lambda functions and services.</td></tr><tr><td>Amazon API Gateway</td><td>Create, secure and manage APIs that front your applications.</td></tr><tr><td>Amazon MQ</td><td>Managed message brokers for apps already using ActiveMQ or RabbitMQ.</td></tr></table>\n<h3>Migration</h3>\n<p>The <b>six migration strategies (the 6 Rs)</b>:</p>\n<table><tr><th>Strategy</th><th>Meaning</th></tr><tr><td>Rehost</td><td>Lift and shift: move servers as they are</td></tr><tr><td>Replatform</td><td>Lift, tinker and shift: small optimizations, like moving to a managed database</td></tr><tr><td>Repurchase</td><td>Switch to a SaaS product</td></tr><tr><td>Refactor</td><td>Re-architect to be cloud-native</td></tr><tr><td>Retire</td><td>Switch off what is no longer needed</td></tr><tr><td>Retain</td><td>Keep it on-premises for now</td></tr></table>\n<table><tr><th>Service</th><th>Purpose</th></tr><tr><td>Migration Hub</td><td>Track migrations across tools</td></tr><tr><td>Application Migration Service</td><td>Rehost servers to AWS</td></tr><tr><td>Database Migration Service (DMS)</td><td>Migrate databases with minimal downtime</td></tr><tr><td>DataSync</td><td>Fast online transfer of large datasets</td></tr><tr><td>Transfer Family</td><td>SFTP/FTPS access to S3 or EFS</td></tr><tr><td>Snow Family</td><td>Physical devices for offline bulk transfer</td></tr></table>\n<p>The <b>AWS Cloud Adoption Framework (CAF)</b> guides organizations through six perspectives: Business, People, Governance, Platform, Security and Operations.</p>"
   },
   {
    "id": "b5",
    "icon": "🤖",
    "title": "4.5 AI and machine learning services",
    "subtitle": "Ready-made intelligence you can call",
    "html": "<table><tr><th>Service</th><th>Use it for</th></tr><tr><td>Amazon SageMaker</td><td>Build, train and deploy your own machine learning models</td></tr><tr><td>Amazon Bedrock</td><td>Access foundation models and build generative AI applications</td></tr><tr><td>Amazon Q</td><td>Generative AI assistant for business and developers</td></tr><tr><td>Amazon Rekognition</td><td>Image and video analysis (faces, objects, text)</td></tr><tr><td>Amazon Comprehend</td><td>Natural language processing: sentiment, entities</td></tr><tr><td>Amazon Textract</td><td>Extract text and data from scanned documents</td></tr><tr><td>Amazon Transcribe / Polly</td><td>Speech to text / text to speech</td></tr><tr><td>Amazon Translate</td><td>Language translation</td></tr><tr><td>Amazon Lex</td><td>Build chatbots and voice interfaces</td></tr><tr><td>Amazon Kendra</td><td>Intelligent enterprise search</td></tr><tr><td>Amazon Forecast / Personalize</td><td>Forecasting and personalized recommendations</td></tr></table>\n<div class=\"tip\"><b>Exam tip:</b> Scanned forms = Textract. Image content = Rekognition. Sentiment of text = Comprehend. Chatbot = Lex. Build your own model = SageMaker.</div>"
   },
   {
    "id": "b6",
    "icon": "🎯",
    "title": "4.6 Exam strategy &amp; common traps",
    "subtitle": "CLF-C02 at a glance",
    "html": "<figure class=\"fig\"><img class=\"aws-img\" src=\"/images/aws-clf/aws-clf-12-exam-domain-weights.svg\" alt=\"Exam domain weights\" width=\"640\" height=\"244\" loading=\"lazy\" style=\"min-width:640px;max-width:640px\"><figcaption>Spend your study time in proportion to the domain weights</figcaption></figure>\n<table><tr><th>Item</th><th>Detail</th></tr><tr><td>Format</td><td>65 questions: multiple choice and multiple response (some are unscored)</td></tr><tr><td>Time</td><td>90 minutes</td></tr><tr><td>Passing score</td><td>700 on a 100 to 1000 scale</td></tr><tr><td>Prerequisites</td><td>None</td></tr></table>\n<h3>How to answer</h3>\n<ol><li>Read the last sentence first to see what is asked (cheapest, most secure, least effort).</li><li>Underline keywords: \"serverless\", \"managed\", \"audit\", \"DDoS\", \"archive\".</li><li>Eliminate two wrong answers, then choose between the rest.</li><li>Flag long questions and return; do not leave any blank.</li></ol>\n<h3>Top traps</h3>\n<ul><li>IAM, Route 53 and CloudFront are global; most other services are regional.</li><li>EBS is one AZ; EFS is multi-AZ.</li><li>CloudTrail = who; CloudWatch = how it performs; Config = how it is configured.</li><li>Shield = DDoS; WAF = web exploits; GuardDuty = threat detection.</li><li>Data in is free; data out costs money.</li><li>Security groups are stateful; NACLs are stateless.</li><li>Direct Connect is private but not encrypted by default.</li><li>Lambda has a 15-minute limit.</li><li>You patch your guest OS; AWS patches the host.</li><li>Spot can be interrupted; Reserved needs a commitment.</li></ul>\n<h3>Your final-week plan</h3>\n<table><tr><th>When</th><th>What</th></tr><tr><td>Days 1-2</td><td>Reread the cheat sheets from Core Services and Security; redo weak quizzes</td></tr><tr><td>Days 3-4</td><td>Two timed full practice exams, then review every wrong answer</td></tr><tr><td>Day 5</td><td>Billing, support and migration revision (Billing, Support & Exam Strategy)</td></tr><tr><td>Day 6</td><td>One last exam; target 80% or higher</td></tr><tr><td>Day 7</td><td>Rest, quick skim of traps, then sit the exam</td></tr></table>\n<div class=\"tip\"><b>Next steps:</b> After passing, consider AWS Solutions Architect Associate (SAA) or Developer Associate (DVA). The Cloud Practitioner builds the vocabulary both rely on.</div>"
   }
  ]
 }
];
export const practiceTopics: PracticeTopic[] = [
 {
  "t": "Shared Responsibility",
  "i": "🤝",
  "s": "Who secures what: AWS vs the customer",
  "q": [
   {
    "q": "Which option is a customer responsibility when using Amazon DynamoDB under the AWS Shared Responsibility Model?",
    "o": [
     "Physical security of DynamoDB",
     "Patching of DynamoDB",
     "Access to DynamoDB tables",
     "Encryption of data at rest in DynamoDB"
    ],
    "a": 2,
    "e": "DynamoDB is a serverless NoSQL key-value database with single-digit millisecond latency.",
    "r": "DynamoDB is a managed (serverless) service, so AWS looks after the hardware, the data centers and the database software. What AWS cannot decide for you is who is allowed to use your data. Controlling access to your tables, through IAM users, roles and policies, is always the customer's job.",
    "w": [
     "A. Physical security of the facilities is AWS's job.",
     "B. AWS patches the DynamoDB software and servers.",
     "D. DynamoDB encrypts data at rest by default using AWS-managed keys, so AWS does that work; the customer only chooses to use a different key if they want to."
    ]
   },
   {
    "q": "A company is using Amazon DynamoDB. Which task is the company’s responsibility, according to the AWS shared responsibility model?",
    "o": [
     "Patch the operating system",
     "Provision hosts",
     "Manage database access permissions",
     "Secure the operating system"
    ],
    "a": 2,
    "e": "",
    "r": "With a managed service like DynamoDB, the customer is responsible for deciding who can read and write the data, which means managing access permissions through IAM. This is the 'security in the cloud' side of shared responsibility.",
    "w": [
     "A and D. DynamoDB has no operating system you can see; AWS runs and patches it.",
     "B. Provisioning and managing the underlying hosts is AWS's responsibility for every managed service."
    ]
   },
   {
    "q": "Which option is a customer responsibility under the AWS shared responsibility model?",
    "o": [
     "Maintenance of underlying hardware of Amazon EC2 instances",
     "Application data security",
     "Physical security of data centers",
     "Maintenance of VPC components"
    ],
    "a": 1,
    "e": "",
    "r": "Application data security is 'security in the cloud': what data you store, who can access it, and whether it is encrypted is up to you. AWS can give you the tools, but you decide how to use them.",
    "w": [
     "A. AWS maintains the physical hardware under EC2.",
     "C. AWS secures the data centers.",
     "D. The VPC infrastructure (the underlying network components) is maintained by AWS; you configure your own VPC settings, but you do not maintain the underlying components."
    ]
   },
   {
    "q": "Which task is a customer’s responsibility, according to the AWS shared responsibility model?",
    "o": [
     "Management of the guest operating systems",
     "Maintenance of the con",
     "Management of the host operating systems and virtualization",
     "Maintenance of the software that powers Availability Zones"
    ],
    "a": 0,
    "e": "",
    "r": "Guest operating systems (for example the Linux or Windows running inside an EC2 instance) belong to the customer. You patch them, configure them and secure them. That is why EC2 gives full control but also more work.",
    "w": [
     "B. This option is truncated in the source question, but it describes an AWS-side task.",
     "C. The host operating system and virtualization layer sit beneath your instance and are managed by AWS.",
     "D. The software that powers Availability Zones is part of the AWS global infrastructure, which AWS operates."
    ]
   },
   {
    "q": "A company needs to transfer data between an Amazon S3 bucket and an on-premises application. Who is responsible for the security of this data, according to the AWS shared responsibility model?",
    "o": [
     "The company",
     "AWS",
     "Firewall vendor",
     "AWS Marketplace partner"
    ],
    "a": 0,
    "e": "",
    "r": "Data you move between your own systems and S3 is your data. The company decides how it is protected in transit (for example with HTTPS/TLS) and at rest, and who may access it. Customer data is always the customer's responsibility.",
    "w": [
     "B. AWS secures the S3 service and its infrastructure, but not the decisions you make about your data.",
     "C and D. Third parties are not part of the shared responsibility model; the model is only between AWS and the customer."
    ]
   },
   {
    "q": "What is the customer ALWAYS responsible for managing, according to the AWS shared responsibility model?",
    "o": [
     "Software licenses",
     "Networking",
     "Customer data",
     "Encryption keys"
    ],
    "a": 2,
    "e": "",
    "r": "Whatever else changes between services, the customer always owns their data: its content, classification, access and protection. Even for fully managed services, the data stays your responsibility.",
    "w": [
     "A. Software licenses depend on what you choose to run.",
     "B. Networking varies: AWS runs the physical network, you configure security groups and VPCs.",
     "D. Key management varies by service, and with services that encrypt automatically AWS can manage the keys. Only 'customer data' is responsibility in every case."
    ]
   },
   {
    "q": "Who enables encryption of data at rest for Amazon Elastic Block Store (Amazon EBS)?",
    "o": [
     "AWS Support",
     "AWS customers",
     "AWS Key Management Service (AWS KMS)",
     "AWS Trusted Advisor"
    ],
    "a": 1,
    "e": "",
    "r": "Encrypting an EBS volume is a customer choice that you turn on when creating the volume (or by enabling default encryption for the account). AWS provides the technology, but the customer decides to enable it.",
    "w": [
     "A. AWS Support helps with issues; it does not configure your resources.",
     "C. KMS manages the keys used for encryption, but it does not decide to encrypt your volumes; you do.",
     "D. Trusted Advisor only gives recommendations and does not change settings."
    ]
   },
   {
    "q": "A company is planning to host its workloads on AWS. Which AWS service requires the company to update and patch the guest operating system?",
    "o": [
     "Amazon DynamoDB",
     "Amazon S3",
     "Amazon EC2",
     "Amazon Aurora"
    ],
    "a": 2,
    "e": "With EC2 you manage the guest OS, including patching.",
    "r": "EC2 is infrastructure as a service (IaaS). You get a virtual server and are responsible for the guest operating system, including installing updates and security patches.",
    "w": [
     "A, B and D. DynamoDB, S3 and Aurora are managed services; AWS patches the operating system and database software underneath, so you never touch a guest OS."
    ]
   },
   {
    "q": "A company is migrating its workloads to the AWS Cloud. The company must retain full control of patch management for the guest operating systems that host its applications. Which AWS service should the company use to meet these requirements?",
    "o": [
     "Amazon DynamoDB",
     "Amazon EC2",
     "AWS Lambda",
     "Amazon RDS"
    ],
    "a": 1,
    "e": "With EC2 you manage the guest OS, including patching.",
    "r": "Full control over guest OS patching means you need a service that gives you access to the operating system. Only EC2 does: you choose when and how to patch.",
    "w": [
     "A, C and D. DynamoDB, Lambda and RDS are managed services where AWS patches the underlying operating system on your behalf, so you cannot retain full control of it."
    ]
   },
   {
    "q": "Which maintenance task is the customer’s responsibility, according to the AWS shared responsibility model?",
    "o": [
     "Physical connectivity among Availability Zones",
     "Network switch maintenance",
     "Hardware updates and",
     "Amazon EC2 updates and security patches"
    ],
    "a": 3,
    "e": "With EC2 you manage the guest OS, including patching.",
    "r": "Updates and security patches for the software you run on EC2, including the guest operating system, are the customer's task. AWS only handles the host and hardware beneath the instance.",
    "w": [
     "A. Connectivity between Availability Zones is AWS infrastructure.",
     "B. Network switches are physical hardware owned and maintained by AWS.",
     "C. Hardware updates (the option is truncated) are AWS's responsibility because the hardware is in AWS data centers."
    ]
   }
  ]
 },
 {
  "t": "Security & IAM",
  "i": "🔒",
  "s": "Identity, access and protection services",
  "q": [
   {
    "q": "A company has an AWS account. The company wants to audit its password and access key rotation details for compliance purposes. Which AWS service or tool will meet this requirement?",
    "o": [
     "IAM Access Analyzer",
     "AWS Artifact",
     "IAM credential report",
     "AWS Audit Manager"
    ],
    "a": 2,
    "e": "",
    "r": "The IAM credential report lists all IAM users in the account and the status of their credentials, including password age, last use, access key age and rotation, and whether MFA is enabled. It is exactly what an auditor asks for.",
    "w": [
     "A. IAM Access Analyzer finds resources shared outside your account, not credential age.",
     "B. AWS Artifact provides AWS's own compliance reports and agreements.",
     "D. Audit Manager collects evidence for audits of your use of AWS but does not report password and key rotation details."
    ]
   },
   {
    "q": "A company needs to block SQL injection attacks. Which AWS service or feature can meet this requirement?",
    "o": [
     "AWS WAF",
     "AWS Shield",
     "Network ACLs",
     "Security groups"
    ],
    "a": 0,
    "e": "WAF blocks common web exploits such as SQL injection and XSS. Shield handles DDoS.",
    "r": "AWS WAF is a web application firewall. It inspects HTTP/HTTPS requests and can block common exploits such as SQL injection and cross-site scripting using rules you configure.",
    "w": [
     "B. Shield protects against DDoS attacks, not SQL injection.",
     "C. Network ACLs work at the subnet level with IP addresses and ports; they do not inspect request contents.",
     "D. Security groups allow or deny traffic by port and source, also without looking at web request contents."
    ]
   },
   {
    "q": "A cloud practitioner needs to obtain AWS compliance reports before migrating an environment to the AWS Cloud. How can these reports be generated?",
    "o": [
     "Contact the AWS Compliance team",
     "Download the reports from AWS Artifact",
     "Openase with AWS Support",
     "Generate the reports with Amazon Macie"
    ],
    "a": 1,
    "e": "AWS Artifact is the portal for compliance reports and agreements.",
    "r": "AWS Artifact is the self-service portal for downloading AWS compliance reports (such as SOC and PCI) and for accepting agreements. A customer can get what they need immediately without contacting anyone.",
    "w": [
     "A and C. There is no need to contact a compliance team or open a support case for standard reports; Artifact exists for this.",
     "D. Macie finds sensitive data in S3, and does not generate AWS compliance reports."
    ]
   },
   {
    "q": "A company is setting up AWS Identity and Access Management (IAM) on an AWS account. Which recommendation complies with IAM security best practices?",
    "o": [
     "Use the account root user access keys for administrative tasks",
     "Grant broad permissions so that all company employees can access the resources they need",
     "Turn on multi-factor authentication (MFA) for added security during the login process",
     "Avoid rotating credentials to prevent issues in production applications"
    ],
    "a": 2,
    "e": "",
    "r": "Turning on multi-factor authentication adds a second proof of identity, so a stolen or guessed password alone is not enough. It is a core IAM best practice, especially for the root user and privileged users.",
    "w": [
     "A. The root user's access keys should not be used for daily work; they should be avoided and the root user locked away.",
     "B. This breaks least privilege; give only the permissions needed.",
     "D. Credentials should be rotated regularly, not left unchanged."
    ]
   },
   {
    "q": "A company is developing an application that uses multiple AWS services. The application needs to use temporary, limited-privilege credentials for authentication with other AWS APIs. Which AWS service or feature should the company use to meet these authentication requirements?",
    "o": [
     "Amazon API Gateway",
     "IAM users",
     "AWS Security Token Service (AWS STS)",
     "IAM instance pro"
    ],
    "a": 2,
    "e": "STS issues temporary, limited-privilege credentials.",
    "r": "AWS Security Token Service (STS) issues temporary, limited-privilege credentials that expire automatically. They are used for roles, federation and cross-account access, so long-term secrets are not stored in applications.",
    "w": [
     "A. API Gateway publishes and manages APIs; it is not a credentials service.",
     "B. IAM users have long-term credentials, not temporary ones.",
     "D. An instance profile (truncated option) passes a role to EC2, but it relies on STS for the actual temporary credentials."
    ]
   },
   {
    "q": "Which AWS service is a cloud security posture management (CSPM) service that aggregates alerts from various AWS services and partner products in a standardized format?",
    "o": [
     "AWS Security Hub",
     "AWS Trusted Advisor",
     "Amazon EventBridge",
     "Amazon GuardDuty"
    ],
    "a": 0,
    "e": "Security Hub aggregates security findings and checks into one view.",
    "r": "AWS Security Hub is a cloud security posture management service. It gathers findings from services such as GuardDuty, Inspector and Macie and from partner products, puts them in a standard format, and gives one view of your security status.",
    "w": [
     "B. Trusted Advisor gives checks on cost, performance, security and limits, but it does not aggregate partner alerts.",
     "C. EventBridge routes events between services.",
     "D. GuardDuty detects threats and is one source that feeds Security Hub."
    ]
   },
   {
    "q": "Which AWS service is always provided at no charge?",
    "o": [
     "Amazon S3",
     "AWS Identity and Access Management (IAM)",
     "Elastic Load Balancers",
     "AWS WAF"
    ],
    "a": 1,
    "e": "",
    "r": "IAM is always free. You pay only for the AWS resources your users create, not for managing users, groups, roles and policies.",
    "w": [
     "A. S3 charges for storage and requests.",
     "C. Elastic Load Balancers charge hourly and for capacity used.",
     "D. WAF charges for web ACLs, rules and requests."
    ]
   },
   {
    "q": "Which AWS service gives users the ability to discover and protect sensitive data that is stored in Amazon S3 buckets?",
    "o": [
     "Amazon Macie",
     "Amazon Detective",
     "Amazon GuardDuty",
     "AWS IAM Access Analyzer"
    ],
    "a": 0,
    "e": "Macie uses machine learning to find sensitive data in S3.",
    "r": "Amazon Macie uses machine learning and pattern matching to discover sensitive data such as personal information in S3 buckets, then helps you protect it with alerts.",
    "w": [
     "B. Detective investigates the cause of security findings.",
     "C. GuardDuty detects threats and suspicious activity, not sensitive data content.",
     "D. IAM Access Analyzer finds resources shared externally, not the type of data inside them."
    ]
   },
   {
    "q": "A company wants to establish a schedule for rotating database user credentials. Which AWS service will support this requirement with the LEAST amount of operational overhead?",
    "o": [
     "AWS Systems Manager",
     "AWS Secrets Manager",
     "AWS License Manager",
     "AWS Managed Services"
    ],
    "a": 1,
    "e": "Secrets Manager stores and automatically rotates credentials.",
    "r": "AWS Secrets Manager stores credentials and can rotate them automatically on a schedule, for example for RDS databases, with almost no custom work.",
    "w": [
     "A. Systems Manager Parameter Store can store secrets, but automatic rotation is the Secrets Manager feature.",
     "C. License Manager tracks software licenses.",
     "D. AWS Managed Services is a service for operating your AWS infrastructure, which would be much more overhead than needed."
    ]
   },
   {
    "q": "A company wants to grant users in one AWS account access to resources in another AWS account. The users do not currently have permission to access the resources. Which AWS service will meet this requirement?",
    "o": [
     "IAM group",
     "IAM role",
     "IAM tag",
     "IAM Access Analyzer"
    ],
    "a": 1,
    "e": "Roles give temporary permissions and work across accounts.",
    "r": "An IAM role gives temporary permissions that can be assumed by users in another account. The target account creates a role that trusts the other account, so no extra users or long-term keys are needed.",
    "w": [
     "A. Groups only collect users within one account.",
     "C. Tags label resources, and do not grant access.",
     "D. IAM Access Analyzer reports on sharing; it does not give permissions."
    ]
   },
   {
    "q": "A social media company wants to protect its web application from common web exploits such as SQL injections and cross-site scripting. Which AWS service will meet these requirements?",
    "o": [
     "Amazon Inspector",
     "AWS WAF",
     "Amazon GuardDuty",
     "Amazon CloudWatch"
    ],
    "a": 1,
    "e": "WAF blocks common web exploits such as SQL injection and XSS. Shield handles DDoS.",
    "r": "AWS WAF is built to protect web applications from common exploits including SQL injection and cross-site scripting by filtering requests with rules.",
    "w": [
     "A. Inspector scans for vulnerabilities in workloads; it does not block live requests.",
     "C. GuardDuty detects threats but does not filter web requests.",
     "D. CloudWatch monitors metrics and logs."
    ]
   },
   {
    "q": "A company wants an automated process to continuously scan its Amazon EC2 instances for software vulnerabilities. Which AWS service will meet these requirements?",
    "o": [
     "Amazon GuardDuty",
     "Amazon Inspector",
     "Amazon Detective",
     "Amazon Cognito"
    ],
    "a": 1,
    "e": "Inspector scans workloads for software vulnerabilities.",
    "r": "Amazon Inspector automatically and continuously scans EC2 instances (and container images and Lambda functions) for software vulnerabilities and unintended network exposure.",
    "w": [
     "A. GuardDuty looks for malicious activity in logs and network flow.",
     "C. Detective helps investigate findings.",
     "D. Cognito manages sign-in for application users."
    ]
   },
   {
    "q": "A company wants to identify Amazon S3 buckets that are shared with another AWS account. Which AWS service or feature will meet these requirements?",
    "o": [
     "AWS Lake Formation",
     "IAM credential report",
     "Amazon CloudWatch",
     "IAM Access Analyzer"
    ],
    "a": 3,
    "e": "IAM Access Analyzer identifies resources shared outside your account or organization.",
    "r": "IAM Access Analyzer examines resource policies and identifies S3 buckets (and other resources) that are shared with another account, with the public or outside your organization.",
    "w": [
     "A. Lake Formation builds data lakes.",
     "B. The credential report is about user credentials.",
     "C. CloudWatch monitors performance, and does not analyze resource sharing."
    ]
   },
   {
    "q": "A company needs a central user portal so that users can log in to third-party business applications that support Security Assertion Markup Language (SAML) 2.0. Which AWS service will meet this requirement?",
    "o": [
     "AWS Identity and Access Management (IAM)",
     "Amazon Cognito",
     "AWS IAM Identity Center (AWS Single Sign-On)",
     "AWS CLI"
    ],
    "a": 2,
    "e": "IAM Identity Center provides single sign-on to accounts and SAML applications.",
    "r": "IAM Identity Center (formerly AWS SSO) provides a central portal where workforce users sign in once and access AWS accounts and SAML 2.0 business applications.",
    "w": [
     "A. IAM manages permissions inside AWS accounts, not a SAML application portal.",
     "B. Cognito is for customers of your own app.",
     "D. The AWS CLI is a command-line tool."
    ]
   },
   {
    "q": "Which AWS service enables users to check for vulnerabilities on Amazon EC2 instances by using prede?",
    "o": [
     "AWS WAF",
     "AWS Trusted Advisor",
     "Amazon Inspector",
     "AWS Shield"
    ],
    "a": 2,
    "e": "Inspector scans workloads for software vulnerabilities.",
    "r": "Amazon Inspector checks EC2 instances for known vulnerabilities and deviations from best practices using predefined rules packages.",
    "w": [
     "A. WAF filters web requests.",
     "B. Trusted Advisor gives high-level recommendations, not instance vulnerability scans.",
     "D. Shield protects against DDoS attacks."
    ]
   },
   {
    "q": "A company has teams that have different job roles and responsibilities. The company’s employees often change teams. The company needs to manage permissions for the employees so that the permissions are appropriate for the job responsibilities. Which IAM resource should the company use to meet this requirement with the LEAST operational overhead?",
    "o": [
     "IAM user groups",
     "IAM roles",
     "IAM instance pro",
     "IAM policies for individual users"
    ],
    "a": 1,
    "e": "Roles give temporary permissions and work across accounts.",
    "r": "IAM roles let you define permissions for a job function once; people take the role that fits their current team. When employees change teams, you change what they can assume instead of editing many user permissions, so overhead is low.",
    "w": [
     "A. User groups also work but permissions must still be reassigned when people change teams; the exam answer for the least overhead with changing responsibilities is roles.",
     "C. Instance profiles (truncated) are for EC2.",
     "D. Per-user policies are the most work to maintain."
    ]
   },
   {
    "q": "Which AWS service can a company use to securely store and encrypt passwords for a database?",
    "o": [
     "AWS Shield",
     "AWS Secrets Manager",
     "AWS Identity and Access Management (IAM)",
     "Amazon Cognito"
    ],
    "a": 1,
    "e": "Secrets Manager stores and automatically rotates credentials.",
    "r": "Secrets Manager securely stores and encrypts secrets such as database passwords, controls access with IAM, and can rotate them.",
    "w": [
     "A. Shield is DDoS protection.",
     "C. IAM controls who can do what but is not designed to store passwords for databases.",
     "D. Cognito manages application user sign-in."
    ]
   },
   {
    "q": "A company wants to integrate its online shopping website with social media login credentials. 0 1 Which AWS service can the company use to make this integration?",
    "o": [
     "AWS Directory Service",
     "AWS Identity and Access Management (IAM)",
     "Amazon Cognito",
     "AWS IAM Identity Center (AWS Single Sign-On)"
    ],
    "a": 2,
    "e": "Cognito handles sign-up, sign-in and social login for your own apps.",
    "r": "Amazon Cognito provides sign-up and sign-in for your own web and mobile apps and supports social identity providers such as Google, Facebook and Amazon.",
    "w": [
     "A. Directory Service is for Microsoft Active Directory.",
     "B. IAM is for AWS users and permissions, not customers of a shop.",
     "D. IAM Identity Center is for workforce access to AWS accounts."
    ]
   },
   {
    "q": "A company website is experiencing DDoS attacks. Which AWS service can help protect the company website against these attacks?",
    "o": [
     "AWS Resource Access Manager",
     "AWS Amplify",
     "AWS Shield",
     "Amazon GuardDuty"
    ],
    "a": 2,
    "e": "Shield protects against DDoS attacks.",
    "r": "AWS Shield protects applications against distributed denial of service (DDoS) attacks. Standard protection is automatic and free; Shield Advanced adds more.",
    "w": [
     "A. Resource Access Manager shares resources between accounts.",
     "B. Amplify builds web and mobile apps.",
     "D. GuardDuty detects threats but does not block DDoS traffic."
    ]
   },
   {
    "q": "A company hosts a large amount of data in AWS. The company wants to identify if any of the data should be considered sensitive. Which AWS service will meet the requirement?",
    "o": [
     "Amazon Inspector",
     "Amazon Macie",
     "AWS Identity and Access Management (IAM)",
     "Amazon CloudWatch"
    ],
    "a": 1,
    "e": "Macie uses machine learning to find sensitive data in S3.",
    "r": "Macie discovers and classifies sensitive data stored in S3, so the company can see which of its large amount of data is sensitive.",
    "w": [
     "A. Inspector scans compute for vulnerabilities.",
     "C. IAM controls access.",
     "D. CloudWatch monitors metrics and logs."
    ]
   },
   {
    "q": "Which AWS service or feature will search for and identify AWS resources that are shared externally?",
    "o": [
     "Amazon OpenSearch Service",
     "AWS Control Tower",
     "AWS IAM Access Analyzer",
     "AWS Fargate"
    ],
    "a": 2,
    "e": "IAM Access Analyzer identifies resources shared outside your account or organization.",
    "r": "IAM Access Analyzer finds resources, such as S3 buckets, IAM roles and KMS keys, that are shared with external entities.",
    "w": [
     "A. OpenSearch Service is a search and analytics engine.",
     "B. Control Tower sets up a multi-account environment.",
     "D. Fargate runs containers."
    ]
   }
  ]
 },
 {
  "t": "Billing, Pricing & Support",
  "i": "💰",
  "s": "Cost tools, purchasing options and support",
  "q": [
   {
    "q": "Which AWS service or tool helps users visualize, understand, and manage spending and usage over time?",
    "o": [
     "AWS Organizations",
     "AWS Pricing Calculator",
     "AWS Cost Explorer",
     "AWS Service Catalog"
    ],
    "a": 2,
    "e": "Cost Explorer visualizes and analyzes past spending and usage.",
    "r": "AWS Cost Explorer gives charts and reports to visualize, understand and manage your costs and usage over time, with filters by service, account or tag, and forecasts.",
    "w": [
     "A. Organizations manages multiple accounts and consolidated billing.",
     "B. Pricing Calculator estimates future costs; it does not show past spend.",
     "D. Service Catalog lets you offer approved products."
    ]
   },
   {
    "q": "A company runs thousands of simultaneous simulations using AWS Batch. Each simulation is stateless, is fault tolerant, and runs for up to 3 hours. Which pricing model enables the company to optimize costs and meet these requirements?",
    "o": [
     "Reserved Instances",
     "Spot Instances",
     "On-Demand Instances",
     "Dedicated Instances"
    ],
    "a": 1,
    "e": "Spot gives the deepest discount (up to 90%) for interruptible, fault-tolerant work.",
    "r": "Spot Instances use spare EC2 capacity at up to about 90% off. The workload here is stateless and fault tolerant, so an interruption does no harm, which is the ideal case for Spot.",
    "w": [
     "A. Reserved Instances need a 1 or 3 year commitment and suit steady workloads.",
     "C. On-Demand costs the most per hour.",
     "D. Dedicated Instances run on single-tenant hardware and cost more."
    ]
   },
   {
    "q": "An ecommerce company has migrated its IT infrastructure from an on-premises data center to the AWS Cloud. Which cost is the company’s direct responsibility?",
    "o": [
     "Cost of application software licenses",
     "Cost of the hardware infrastructure on AWS",
     "Cost of power for the AWS servers",
     "Cost of physical security for the AWS data center"
    ],
    "a": 0,
    "e": "",
    "r": "After moving to AWS, the company still pays for and manages the software it brings, such as application software licenses. That cost stays with the customer.",
    "w": [
     "B. Hardware cost is built into the AWS prices; you do not buy the hardware.",
     "C. Power for the servers is AWS's cost.",
     "D. Physical security of the data centers is AWS's responsibility."
    ]
   },
   {
    "q": "A company is exploring the use of the AWS Cloud, and needs to create a cost estimate for a project before the infrastructure is provisioned. Which AWS service or feature can be used to estimate costs before deployment?",
    "o": [
     "AWS Free Tier",
     "AWS Pricing Calculator",
     "AWS Billing and Cost Management",
     "AWS Cost and Usage Report"
    ],
    "a": 1,
    "e": "The Pricing Calculator estimates costs before you build; Cost Explorer analyzes past spending.",
    "r": "The AWS Pricing Calculator lets you model services and configurations and see an estimate before anything is provisioned.",
    "w": [
     "A. The Free Tier gives limited free usage and is not an estimator.",
     "C. Billing and Cost Management shows actual spend.",
     "D. The Cost and Usage Report gives detailed data about costs already incurred."
    ]
   },
   {
    "q": "A company has a test AWS environment. A company is planning on testing an application within AWS. The application testing can be interrupted and does not need to run continuously. Which Amazon EC2 purchasing option will meet these requirements MOST cost-effectively?",
    "o": [
     "On-Demand Instances",
     "Dedicated Instances",
     "Spot Instances",
     "Reserved Instances"
    ],
    "a": 2,
    "e": "Spot gives the deepest discount (up to 90%) for interruptible, fault-tolerant work.",
    "r": "Because the testing can be interrupted and need not run continuously, Spot Instances give the largest discount and are the most cost-effective.",
    "w": [
     "A. On-Demand costs more.",
     "B. Dedicated Instances cost more and are used for isolation needs.",
     "D. Reserved Instances need a commitment and are for continuous workloads."
    ]
   },
   {
    "q": "A company plans to migrate to AWS and wants to create cost estimates for its AWS use cases. Which AWS service or tool can the company use to meet these requirements?",
    "o": [
     "AWS Pricing Calculator",
     "Amazon CloudWatch",
     "AWS Cost Explorer",
     "AWS Budgets"
    ],
    "a": 0,
    "e": "The Pricing Calculator estimates costs before you build; Cost Explorer analyzes past spending.",
    "r": "The AWS Pricing Calculator creates cost estimates for planned AWS use cases before migration or deployment.",
    "w": [
     "B. CloudWatch monitors metrics.",
     "C. Cost Explorer analyzes costs already incurred.",
     "D. Budgets sets spending thresholds and alerts, not estimates for a new design."
    ]
   },
   {
    "q": "A company wants to run its workload on Amazon EC2 instances for more than 1 year. This workload will run continuously. Which option offers a discounted hourly rate compared to the hourly rate of On-Demand Instances?",
    "o": [
     "AWS Graviton processor",
     "Dedicated Hosts",
     "EC2 Instance Savings Plans",
     "Amazon EC2 Auto Scaling instances"
    ],
    "a": 2,
    "e": "Savings Plans discount committed hourly spend for 1 or 3 years.",
    "r": "A workload that runs continuously for more than a year fits a commitment-based discount. EC2 Instance Savings Plans give a lower hourly rate than On-Demand in return for a 1 or 3 year commitment to usage.",
    "w": [
     "A. Graviton processors can be cheaper, but they are a processor type, not a pricing option.",
     "B. Dedicated Hosts are for licensing and compliance and cost more.",
     "D. Auto Scaling changes the number of instances; it is not a discount."
    ]
   },
   {
    "q": "Which AWS Cloud service can send alerts to customers if custom spending thresholds are exceeded?",
    "o": [
     "AWS Budgets",
     "AWS Cost Explorer",
     "AWS Cost Allocation Tags",
     "AWS Organizations"
    ],
    "a": 0,
    "e": "AWS Budgets alerts you when cost or usage crosses a threshold.",
    "r": "AWS Budgets lets you set custom cost or usage budgets and sends alerts when spending reaches or is forecast to exceed the thresholds.",
    "w": [
     "B. Cost Explorer analyzes spend but is not the alerting tool.",
     "C. Cost allocation tags categorize costs.",
     "D. Organizations manages accounts."
    ]
   },
   {
    "q": "A company needs to continuously run an experimental workload on an Amazon EC2 instance and stop the instance after 12 hours. Which instance purchasing option will meet this requirement MOST cost-effectively?",
    "o": [
     "On-Demand Instances",
     "Reserved Instances",
     "Spot Instances",
     "Dedicated Instances"
    ],
    "a": 0,
    "e": "On-Demand has no commitment and suits short-term or unpredictable workloads.",
    "r": "On-Demand has no commitment and no minimum, so it suits a short-term workload that is started and stopped within 12 hours.",
    "w": [
     "B. Reserved Instances require a long-term commitment, a waste for 12 hours.",
     "C. Spot Instances can be interrupted, which is not appropriate when the workload must run continuously until stopped.",
     "D. Dedicated Instances cost more."
    ]
   },
   {
    "q": "A company that uses AWS needs to transfer 2 TB of data. Which type of transfer of that data would result in no cost for the company?",
    "o": [
     "Inbound data transfer from the internet",
     "Outbound data transfer to the internet",
     "Data transfer between AWS Regions",
     "Data transfer between Availability Zones"
    ],
    "a": 0,
    "e": "Data transfer into AWS from the internet is free; outbound is charged.",
    "r": "Data transferred into AWS from the internet is free. You pay for outbound data to the internet and, in most cases, for data moving between Regions.",
    "w": [
     "B. Outbound transfer to the internet is charged.",
     "C. Data transfer between Regions is charged.",
     "D. Data transfer between Availability Zones is also charged for most services."
    ]
   },
   {
    "q": "A company wants to create Amazon QuickSight dashboards every week by using its billing data. Which AWS feature or tool can the company use to meet these requirements?",
    "o": [
     "AWS Budgets",
     "AWS Cost Explorer",
     "AWS Cost and Usage Report",
     "AWS Cost Anomaly Detection"
    ],
    "a": 2,
    "e": "",
    "r": "The AWS Cost and Usage Report contains the most detailed billing data and can be delivered to S3 on a schedule, which Amazon QuickSight can use to build dashboards.",
    "w": [
     "A. Budgets sets limits and alerts.",
     "B. Cost Explorer has its own charts but is not the data source for QuickSight dashboards.",
     "D. Cost Anomaly Detection finds unusual spend."
    ]
   },
   {
    "q": "A company needs to organize its resources and track AWS costs on a detailed level. The company needs to categorize costs by business department, environment, and application. Which solution will meet these requirements?",
    "o": [
     "Access the AWS Cost Management console to organize resources, set an AWS budget, and receive noti",
     "Use tags to organize the resources. Activate cost allocation tags to track AWS costs onetailed level.",
     "Create Amazon CloudWatch dashboards to visually organize and track costs individually",
     "Access the AWS Billing and Cost Management dashboard to organize and track resource consumption onetailed level"
    ],
    "a": 1,
    "e": "Cost allocation tags let you break down costs by team, project or environment.",
    "r": "Tags are labels such as Department, Environment and Application. After you activate them as cost allocation tags, they appear in billing reports so you can break costs down in detail.",
    "w": [
     "A and D. Cost Management and Billing consoles show costs but cannot categorize by your own business labels without tags.",
     "C. CloudWatch dashboards monitor performance, not costs by category."
    ]
   },
   {
    "q": "Which Amazon EC2 instance pricing model can provide discounts of up to 90%?",
    "o": [
     "Reserved Instances",
     "On-Demand",
     "Dedicated Hosts",
     "Spot Instances"
    ],
    "a": 3,
    "e": "Spot gives the deepest discount (up to 90%) for interruptible, fault-tolerant work.",
    "r": "Spot Instances use unused EC2 capacity at discounts of up to about 90% compared with On-Demand.",
    "w": [
     "A. Reserved Instances save up to about 72%.",
     "B. On-Demand is the full price.",
     "C. Dedicated Hosts cost more because you reserve a physical server."
    ]
   },
   {
    "q": "A company wants a customized assessment of its current on-premises environment. The company wants to understand its projected running costs in the AWS Cloud. Which AWS service or tool will meet these requirements?",
    "o": [
     "AWS Trusted Advisor",
     "Amazon Inspector",
     "AWS Control Tower",
     "Migration Evaluator"
    ],
    "a": 3,
    "e": "",
    "r": "Migration Evaluator analyzes the company's on-premises environment and builds a business case with projected AWS running costs.",
    "w": [
     "A. Trusted Advisor checks an existing AWS account.",
     "B. Inspector scans for vulnerabilities.",
     "C. Control Tower sets up a governed multi-account environment."
    ]
   },
   {
    "q": "A user has a stateful workload that will run on Amazon EC2 for the next 3 years. What is the MOST cost-effective pricing model for this workload?",
    "o": [
     "On-Demand Instances",
     "Reserved Instances",
     "Dedicated Instances",
     "Spot Instances"
    ],
    "a": 1,
    "e": "Reserved Instances discount steady, predictable usage in return for a 1 or 3 year commitment.",
    "r": "A stateful workload that runs steadily for 3 years benefits most from a Reserved Instance (or a Savings Plan) with a 3 year commitment, which gives the largest discount for predictable use.",
    "w": [
     "A. On-Demand is the most expensive for long steady use.",
     "C. Dedicated Instances cost more.",
     "D. Spot Instances can be interrupted, which is risky for stateful workloads."
    ]
   },
   {
    "q": "A company often does not use all of its current Amazon EC2 capacity to run stateless workloads. The company wants to optimize its EC2 costs. Which EC2 instance type will meet these requirements?",
    "o": [
     "Spot Instances",
     "Dedicated Instances",
     "Reserved Instances",
     "On-Demand Instances"
    ],
    "a": 0,
    "e": "Spot gives the deepest discount (up to 90%) for interruptible, fault-tolerant work.",
    "r": "The workloads are stateless, so an interruption does not lose data, and the company does not use all its capacity. Spot Instances give the lowest cost for this interruption-tolerant work.",
    "w": [
     "B. Dedicated Instances cost more.",
     "C. Reserved Instances pay off only for steady, predictable usage that is fully used.",
     "D. On-Demand does not optimize cost."
    ]
   }
  ]
 },
 {
  "t": "Cloud Concepts & Frameworks",
  "i": "☁️",
  "s": "Benefits, Well-Architected and CAF",
  "q": [
   {
    "q": "Which option is a perspective that includes foundational capabilities of the AWS Cloud Adoption Framework (AWS CAF)?",
    "o": [
     "Sustainability",
     "Performance",
     "Governance",
     "Reliability"
    ],
    "a": 2,
    "e": "Governance is one of the six CAF perspectives (with Business, People, Platform, Security, Operations).",
    "r": "The AWS Cloud Adoption Framework (CAF) helps organizations plan their move to the cloud through six perspectives: Business, People, Governance, Platform, Security and Operations. Governance covers the foundational capabilities for managing risk, programs and cost across the cloud journey.",
    "w": [
     "A. Sustainability is a pillar of the Well-Architected Framework, not a CAF perspective.",
     "B. Performance Efficiency is also a Well-Architected pillar.",
     "D. Reliability is a Well-Architected pillar too. These are a common mix-up with the CAF perspectives."
    ]
   },
   {
    "q": "Which option is a physical location of the AWS global infrastructure?",
    "o": [
     "AWS DataSync",
     "AWS Region",
     "Amazon Connect",
     "AWS Organizations"
    ],
    "a": 1,
    "e": "A Region is a geographic area containing multiple AZs.",
    "r": "The AWS global infrastructure is made of Regions (geographic areas), Availability Zones (data centers inside a Region) and edge locations. An AWS Region is a physical location where AWS has clusters of data centers.",
    "w": [
     "A. AWS DataSync is a data transfer service.",
     "C. Amazon Connect is a cloud contact center.",
     "D. AWS Organizations is an account management service. None of these is a place."
    ]
   },
   {
    "q": "A company wants to protect its AWS Cloud information, systems, and assets while performing risk assessment and mitigation tasks. Which pillar of the AWS Well-Architected Framework is supported by these goals?",
    "o": [
     "Reliability",
     "Security",
     "Operational excellence",
     "Performance"
    ],
    "a": 1,
    "e": "",
    "r": "Protecting information, systems and assets, together with risk assessment and mitigation, is the exact description of the Security pillar of the Well-Architected Framework. It includes identity management, data protection, detection and incident response.",
    "w": [
     "A. Reliability is about recovering from failure and meeting demand.",
     "C. Operational excellence is about running workloads, monitoring and improving procedures.",
     "D. Performance efficiency is about choosing the right resources and using them effectively."
    ]
   },
   {
    "q": "A user needs programmatic access to AWS resources through the AWS CLI or the AWS API. Which option will provide the user with the appropriate access?",
    "o": [
     "Amazon Inspector",
     "Access keys",
     "SSH public keys",
     "AWS Key Management Service (AWS KMS) keys"
    ],
    "a": 1,
    "e": "Access keys are long-term credentials for programmatic (CLI/API) access.",
    "r": "Access keys, an access key ID plus a secret access key, are long-term credentials a user uses to sign requests sent through the AWS CLI, SDKs or API. They provide programmatic access, while a password is for the console.",
    "w": [
     "A. Inspector is a vulnerability scanning service.",
     "C. SSH public keys are for logging in to Linux instances, not for AWS API calls.",
     "D. KMS keys encrypt data and do not authenticate a user."
    ]
   },
   {
    "q": "A company wants to migrate its on-premises relational databases to the AWS Cloud. The company wants to use infrastructure as close to its current geographical location as possible. Which AWS service or resource should the company use to select its Amazon RDS deployment area?",
    "o": [
     "Amazon Connect",
     "AWS Wavelength",
     "AWS Regions",
     "AWS Direct Connect"
    ],
    "a": 2,
    "e": "A Region is a geographic area containing multiple AZs.",
    "r": "When you create an Amazon RDS database you choose the AWS Region in which it runs. Selecting the Region nearest to the company's current location keeps latency low and can help with data residency.",
    "w": [
     "A. Amazon Connect is a contact center service.",
     "B. AWS Wavelength embeds compute in 5G networks.",
     "D. AWS Direct Connect is a dedicated network link; it does not choose where the database is deployed."
    ]
   },
   {
    "q": "Which of the following is a recommended design principle of the AWS Well-Architected Framework?",
    "o": [
     "Reduce downtime by making infrastructure changes infrequently and in large increments",
     "Invest the time to con",
     "Learn to improve from operational failures",
     "Use monolithic application design for centralization"
    ],
    "a": 2,
    "e": "",
    "r": "Well-Architected design principles in operational excellence include performing operations as code, making frequent small reversible changes and learning from operational failures. Reviewing failures and sharing the lessons makes the system better over time.",
    "w": [
     "A. The framework recommends frequent, small, reversible changes, not rare large ones.",
     "B. This option is truncated in the source question.",
     "D. Monolithic designs are discouraged; the framework prefers loosely coupled components."
    ]
   },
   {
    "q": "A company is running a workload in the AWS Cloud. Which AWS best practice ensures the MOST cost-effective architecture for the workload?",
    "o": [
     "Loose coupling",
     "Rightsizing",
     "Caching",
     "Redundancy"
    ],
    "a": 1,
    "e": "Rightsizing matches resource size to actual usage to avoid waste.",
    "r": "Rightsizing means matching instance types and sizes to what the workload actually needs. Paying only for the capacity you use is the best-practice step for the most cost-effective architecture, and tools like Compute Optimizer help.",
    "w": [
     "A. Loose coupling improves resilience and scaling.",
     "C. Caching improves performance.",
     "D. Redundancy improves availability but typically increases cost, so it is not the cost-saving practice."
    ]
   },
   {
    "q": "Which characteristic of the AWS Cloud helps users eliminate underutilized CPU capacity?",
    "o": [
     "Agility",
     "Elasticity",
     "Reliability",
     "Durability"
    ],
    "a": 1,
    "e": "Elasticity means scaling out and back in as demand changes.",
    "r": "Elasticity is the ability to grow and shrink capacity automatically with demand. Because resources shrink when demand falls, you do not pay for idle CPU, which removes underutilized capacity.",
    "w": [
     "A. Agility is speed of trying and launching ideas.",
     "C. Reliability is the ability to recover and keep working.",
     "D. Durability is how unlikely data is to be lost."
    ]
   },
   {
    "q": "A company wants to modernize and convert a monolithic application into microservices. The company wants to move the application to AWS. Which migration strategy should the company use?",
    "o": [
     "Rehost",
     "Replatform",
     "Repurchase",
     "Refactor"
    ],
    "a": 3,
    "e": "Refactor re-architects an application to be cloud-native.",
    "r": "Converting a monolith into microservices means redesigning the application for the cloud, which is the Refactor (re-architect) strategy. It is the most effort but gains the most cloud benefits such as scalability and agility.",
    "w": [
     "A. Rehost (lift and shift) moves it as it is.",
     "B. Replatform makes small optimizations, such as moving to a managed database.",
     "C. Repurchase replaces it with a different product, often SaaS."
    ]
   },
   {
    "q": "Which option is an environment that consists of one or more data centers?",
    "o": [
     "Amazon CloudFront",
     "Availability Zone",
     "VPC",
     "AWS Outposts"
    ],
    "a": 1,
    "e": "An AZ is one or more data centers inside a Region.",
    "r": "An Availability Zone is one or more discrete data centers with redundant power, networking and cooling, located inside a Region. It is the environment made of one or more data centers.",
    "w": [
     "A. CloudFront is a content delivery network.",
     "C. A VPC is a virtual network, not a physical building.",
     "D. Outposts is AWS infrastructure installed at the customer's site."
    ]
   },
   {
    "q": "Which of the following is a recommended design principle for AWS Cloud architecture?",
    "o": [
     "Design tightly coupled components",
     "Buildingle application component that can handle all the application functionality",
     "Make large changes on fewer iterations to reduce chances of failure",
     "Avoid monolithic architecture by segmenting workloads"
    ],
    "a": 3,
    "e": "",
    "r": "Recommended cloud designs avoid monolithic architecture by splitting a workload into smaller, loosely coupled pieces that can be scaled, changed and fail independently.",
    "w": [
     "A. Tightly coupled components fail together.",
     "B. A single component handling everything is a monolith.",
     "C. The recommended approach is making many small changes to reduce risk."
    ]
   },
   {
    "q": "Which of the following is an AWS value proposition that describes a user’s ability to scale infrastructure based on demand?",
    "o": [
     "Speed of innovation",
     "Resource elasticity",
     "Decoupled architecture",
     "Global deployment"
    ],
    "a": 1,
    "e": "Elasticity means scaling out and back in as demand changes.",
    "r": "Resource elasticity is the AWS value proposition of scaling infrastructure up or down automatically with demand, so you do not guess capacity.",
    "w": [
     "A. Speed of innovation is about launching faster.",
     "C. Decoupled architecture is a design approach.",
     "D. Global deployment is about serving users in many Regions."
    ]
   },
   {
    "q": "A company must be able to develop, test, and launch an application in the AWS Cloud quickly. Which advantage of cloud computing will meet these requirements?",
    "o": [
     "Stop guessing capacity",
     "Trade",
     "Achieve economies of scale",
     "Increase speed and agility"
    ],
    "a": 3,
    "e": "Resources arrive in minutes, so teams can build and launch quickly.",
    "r": "Increasing speed and agility means new resources can be created in minutes, so a company can develop, test and launch an application quickly and try new ideas at low cost.",
    "w": [
     "A. Stop guessing capacity is about right-sizing for demand.",
     "B. The truncated option refers to trading fixed expense for variable expense.",
     "C. Economies of scale lowers prices through high volume, not launch speed."
    ]
   },
   {
    "q": "Which cloud concept is demonstrated by using AWS Compute Optimizer?",
    "o": [
     "Security validation",
     "Rightsizing",
     "Elasticity",
     "Global reach"
    ],
    "a": 1,
    "e": "Rightsizing matches resource size to actual usage to avoid waste.",
    "r": "AWS Compute Optimizer analyzes usage of EC2, EBS, Lambda and other resources and recommends better sizes. Choosing resource sizes that match real needs is rightsizing.",
    "w": [
     "A. Compute Optimizer does not validate security.",
     "C. Elasticity is scaling automatically with demand.",
     "D. Global reach is deploying in many Regions."
    ]
   }
  ]
 },
 {
  "t": "Compute",
  "i": "🖥️",
  "s": "EC2, containers and serverless",
  "q": [
   {
    "q": "A company is running and managing its own Docker environment on Amazon EC2 instances. The company wants an alternative to help manage cluster size, scheduling, and environment maintenance. Which AWS service meets these requirements?",
    "o": [
     "AWS Lambda",
     "Amazon RDS",
     "AWS Fargate",
     "Amazon Athena"
    ],
    "a": 2,
    "e": "Fargate runs containers without you managing servers or clusters.",
    "r": "AWS Fargate is a serverless compute engine for containers that works with ECS and EKS. It removes the work of managing cluster size, scheduling and the underlying servers, which is what the company wants to avoid.",
    "w": [
     "A. Lambda runs functions, not Docker clusters.",
     "B. RDS is a managed database.",
     "D. Athena queries data in S3 with SQL."
    ]
   },
   {
    "q": "A developer wants to deploy an application quickly on AWS without manually creating the required resources. Which AWS service will meet these requirements?",
    "o": [
     "Amazon EC2",
     "AWS Elastic Beanstalk",
     "AWS CodeBuild",
     "Amazon Personalize"
    ],
    "a": 1,
    "e": "Elastic Beanstalk deploys code and handles provisioning, scaling and load balancing.",
    "r": "AWS Elastic Beanstalk takes your uploaded application code and automatically handles provisioning, load balancing, auto scaling and health monitoring, so a developer deploys quickly without creating each resource by hand.",
    "w": [
     "A. EC2 requires creating and configuring the resources yourself.",
     "C. CodeBuild compiles and tests source code.",
     "D. Amazon Personalize is a recommendation service."
    ]
   },
   {
    "q": "Which AWS service supports a hybrid architecture that gives users the ability to extend AWS infrastructure, AWS services, APIs, and tools to data centers, co-location environments, or on-premises facilities?",
    "o": [
     "AWS Snowmobile",
     "AWS Local Zones",
     "AWS Outposts",
     "AWS Fargate"
    ],
    "a": 2,
    "e": "Outposts runs AWS infrastructure in your own data center (hybrid).",
    "r": "AWS Outposts installs AWS infrastructure, services, APIs and tools in the customer's own data center or co-location space, giving a consistent hybrid experience.",
    "w": [
     "A. Snowmobile is a truck for moving exabytes of data.",
     "B. Local Zones put AWS compute near large cities but remain AWS-operated.",
     "D. Fargate runs containers in AWS."
    ]
   },
   {
    "q": "Which fully managed AWS service assists with the creation, testing, and management of custom Amazon EC2 images?",
    "o": [
     "EC2 Image Builder",
     "Amazon Machine Image (AMI)",
     "AWS Launch Wizard",
     "AWS Elastic Beanstalk"
    ],
    "a": 0,
    "e": "EC2 Image Builder automates building, testing and maintaining AMIs.",
    "r": "EC2 Image Builder is a fully managed service that automates creating, testing, patching and distributing custom Amazon Machine Images (AMIs).",
    "w": [
     "B. An AMI is the image that is produced, not the service that builds it.",
     "C. Launch Wizard guides deployment of specific applications.",
     "D. Elastic Beanstalk deploys applications."
    ]
   },
   {
    "q": "A company needs to perform data processing once a week that typically takes about 5 hours to complete. Which AWS service should the company use for this workload?",
    "o": [
     "AWS Lambda",
     "Amazon EC2",
     "AWS CodeDeploy",
     "AWS Wavelength"
    ],
    "a": 1,
    "e": "With EC2 you manage the guest OS, including patching.",
    "r": "A job that runs about 5 hours once a week fits EC2, which has no run-time limit and can be started and stopped (or scheduled) as needed, so you pay only while it runs.",
    "w": [
     "A. Lambda functions can run for at most 15 minutes.",
     "C. CodeDeploy automates deployments.",
     "D. Wavelength is for low-latency 5G applications."
    ]
   },
   {
    "q": "A company has an application with robust hardware requirements. The application must be accessed by students who are using lightweight, low- cost laptops. Which AWS service will help the company deploy the application without investing in backend infrastructure or high-end client hardware?",
    "o": [
     "Amazon AppStream 2.0",
     "AWS AppSync",
     "Amazon WorkLink",
     "AWS Elastic Beanstalk"
    ],
    "a": 0,
    "e": "AppStream 2.0 streams desktop applications to a browser.",
    "r": "Amazon AppStream 2.0 streams desktop applications from AWS to a web browser. Heavy applications run on powerful AWS hardware while students use lightweight laptops, with no backend infrastructure to build.",
    "w": [
     "B. AWS AppSync is a managed GraphQL service.",
     "C. WorkLink gave secure mobile access to internal websites.",
     "D. Elastic Beanstalk deploys web applications but does not stream them."
    ]
   },
   {
    "q": "A company wants to automatically add and remove Amazon EC2 instances. The company wants the EC2 instances to adjust to varying workloads dynamically. Which service or feature will meet these requirements?",
    "o": [
     "Amazon DynamoDB",
     "Amazon EC2 Spot Instances",
     "AWS Snow Family",
     "Amazon EC2 Auto Scaling"
    ],
    "a": 3,
    "e": "Auto Scaling adds or removes EC2 instances to match demand.",
    "r": "Amazon EC2 Auto Scaling launches and terminates instances automatically based on demand or schedules, keeping the right number running for the workload.",
    "w": [
     "A. DynamoDB is a database.",
     "B. Spot Instances are a purchase option, not a scaling feature.",
     "C. The Snow Family is for moving data offline."
    ]
   },
   {
    "q": "A user has limited knowledge of AWS services, but wants to quickly deploy a scalable Node.js application in the AWS Cloud. Which service should be used to deploy the application?",
    "o": [
     "AWS CloudFormation",
     "AWS Elastic Beanstalk",
     "Amazon EC2",
     "AWS OpsWorks"
    ],
    "a": 1,
    "e": "Elastic Beanstalk deploys code and handles provisioning, scaling and load balancing.",
    "r": "Elastic Beanstalk supports Node.js and handles capacity provisioning, load balancing and scaling. A user with limited AWS knowledge can just upload code.",
    "w": [
     "A. CloudFormation needs templates, which require AWS knowledge.",
     "C. EC2 means setting up and managing everything yourself.",
     "D. OpsWorks needs Chef or Puppet knowledge."
    ]
   },
   {
    "q": "A company needs to plan, schedule, and run hundreds of thousands of computing jobs on AWS. Which AWS service can the company use to meet this requirement?",
    "o": [
     "AWS Step Functions",
     "AWS Service Catalog",
     "Amazon Simple Queue Service (Amazon SQS)",
     "AWS Batch"
    ],
    "a": 3,
    "e": "AWS Batch runs very large numbers of batch jobs.",
    "r": "AWS Batch plans, schedules and runs batch computing jobs at any scale, choosing the right amount and type of compute and queuing jobs.",
    "w": [
     "A. Step Functions orchestrates workflows.",
     "B. Service Catalog manages approved products.",
     "C. SQS queues messages but does not schedule or run computing jobs."
    ]
   },
   {
    "q": "Which AWS service can a company use to visually design and build serverless applications?",
    "o": [
     "AWS Lambda",
     "AWS Batch",
     "AWS Application Composer",
     "AWS App Runner"
    ],
    "a": 2,
    "e": "",
    "r": "AWS Application Composer gives a visual canvas to design and build serverless applications, connecting services like Lambda, API Gateway and DynamoDB, and generates the infrastructure template.",
    "w": [
     "A. Lambda runs code.",
     "B. Batch runs batch jobs.",
     "D. App Runner deploys containerized web apps."
    ]
   }
  ]
 },
 {
  "t": "Storage",
  "i": "🗄️",
  "s": "S3, hybrid storage and data transfer",
  "q": [
   {
    "q": "A company is storing sensitive customer data in an Amazon S3 bucket. The company wants to protect the data from accidental deletion or overwriting. Which S3 feature should the company use to meet these requirements?",
    "o": [
     "S3 Lifecycle rules",
     "S3 Versioning",
     "S3 bucket policies",
     "S3 server-side encryption"
    ],
    "a": 1,
    "e": "Versioning keeps previous object versions so you can recover deleted or overwritten data.",
    "r": "S3 Versioning keeps every version of an object. If an object is deleted or overwritten by mistake, the earlier version remains and can be restored. It is the feature designed for accidental deletion and overwrite.",
    "w": [
     "A. Lifecycle rules move or expire objects over time.",
     "C. Bucket policies control who can access.",
     "D. Server-side encryption protects confidentiality but not against deleting or overwriting."
    ]
   },
   {
    "q": "Which AWS service is a hybrid cloud storage service that provides on-premises users access to virtually unlimited cloud storage?",
    "o": [
     "AWS DataSync",
     "Amazon S3 Glacier",
     "AWS Storage Gateway",
     "Amazon Elastic Block Store (Amazon EBS)"
    ],
    "a": 2,
    "e": "Storage Gateway connects on-premises systems with AWS storage.",
    "r": "AWS Storage Gateway is a hybrid cloud storage service that gives on-premises users and applications access to practically unlimited cloud storage through standard protocols.",
    "w": [
     "A. DataSync moves data.",
     "B. S3 Glacier is archival storage.",
     "D. EBS is block storage attached to EC2."
    ]
   },
   {
    "q": "A company wants to migrate its NFS on-premises workload to AWS. Which AWS Storage Gateway type should the company use to meet this requirement?",
    "o": [
     "Tape Gateway",
     "Volume Gateway",
     "Amazon FSx File Gateway",
     "Amazon S3 File Gateway"
    ],
    "a": 3,
    "e": "",
    "r": "S3 File Gateway presents an NFS (or SMB) file share to on-premises applications and stores the files as objects in Amazon S3, which suits migrating an NFS workload.",
    "w": [
     "A. Tape Gateway replaces physical tape backups.",
     "B. Volume Gateway presents block volumes via iSCSI.",
     "C. FSx File Gateway gives access to Amazon FSx for Windows File Server shares."
    ]
   },
   {
    "q": "Which of the following describes some of the core functionality of Amazon S3?",
    "o": [
     "Amazon S3 isigh-performance block storage service that is designed for use with Amazon EC2",
     "Amazon S3 is an object storage service that provides high-level performance, security, scalability, and data availability",
     "Amazon S3 isully managed, highly reliable, and scalable",
     "Amazon S3 iscalable, fully managed elastic NFS for use with AWS Cloud services and on-premises resources"
    ],
    "a": 1,
    "e": "",
    "r": "Amazon S3 is an object storage service offering industry-leading scalability, data availability, security and performance, for storing and retrieving any amount of data.",
    "w": [
     "A. This describes Amazon EBS, the block storage service for EC2, not S3.",
     "C. This is a generic claim that does not describe object storage.",
     "D. This describes Amazon EFS, a managed elastic NFS file system."
    ]
   },
   {
    "q": "A company wants to store data in Amazon S3. The company rarely access the data, and the data can be regenerated if necessary. The company wants to store the data in the most cost-effective storage class. Which S3 storage class will meet this requirement?",
    "o": [
     "S3 Standard",
     "S3 Intelligent-Tiering",
     "S3 Standard-Infrequent Access (S3 Standard-IA)",
     "S3 One Zone-Infrequent Access (S3 One Zone-IA)"
    ],
    "a": 3,
    "e": "One Zone-IA stores data in one AZ at lower cost.",
    "r": "S3 One Zone-IA is the lowest-cost class for infrequently accessed data that is stored in a single Availability Zone. Because the data can be regenerated if the AZ is lost, the lower resilience is acceptable.",
    "w": [
     "A. Standard is for frequent access and costs more.",
     "B. Intelligent-Tiering adds a monitoring fee and suits unknown patterns.",
     "C. Standard-IA stores across multiple AZs, so it costs more than One Zone-IA."
    ]
   }
  ]
 },
 {
  "t": "Databases & Analytics",
  "i": "🧮",
  "s": "Relational, NoSQL and analytics",
  "q": [
   {
    "q": "A company has 5 TB of data stored in Amazon S3. The company plans to occasionally run queries on the data for analysis. Which AWS service should the company use to run these queries in the MOST cost-effective manner?",
    "o": [
     "Amazon Redshift",
     "Amazon Athena",
     "Amazon Kinesis",
     "Amazon RDS"
    ],
    "a": 1,
    "e": "Athena runs SQL queries directly on S3 data with no servers.",
    "r": "Amazon Athena is serverless and queries data directly in S3 using standard SQL. You pay per query based on the data scanned, and there is nothing to provision, so occasional analysis of 5 TB is cheapest.",
    "w": [
     "A. Redshift needs a cluster (or serverless capacity) and suits frequent, heavy analytics.",
     "C. Kinesis processes streaming data.",
     "D. RDS requires loading the data into a database and paying for it to run."
    ]
   },
   {
    "q": "To reduce costs, a company is planning to migrate a NoSQL database to AWS. Which AWS service is fully managed and can automatically scale throughput capacity to meet database workload demands?",
    "o": [
     "Amazon Redshift",
     "Amazon Aurora",
     "Amazon DynamoDB",
     "Amazon RDS"
    ],
    "a": 2,
    "e": "DynamoDB is a serverless NoSQL key-value database with single-digit millisecond latency.",
    "r": "DynamoDB is a fully managed NoSQL database that scales throughput automatically (on-demand or auto scaling mode), so a NoSQL migration needs no servers or capacity planning.",
    "w": [
     "A. Redshift is a data warehouse, not NoSQL.",
     "B and D. Aurora and RDS are relational (SQL) databases, not NoSQL."
    ]
   },
   {
    "q": "Which of the following is a fully managed MySQL-compatible database?",
    "o": [
     "Amazon S3",
     "Amazon DynamoDB",
     "Amazon Redshift",
     "Amazon Aurora"
    ],
    "a": 3,
    "e": "Aurora is a managed relational database compatible with MySQL and PostgreSQL.",
    "r": "Amazon Aurora is a fully managed relational database compatible with MySQL (and PostgreSQL), built by AWS for higher performance and availability.",
    "w": [
     "A. S3 is object storage.",
     "B. DynamoDB is NoSQL.",
     "C. Redshift is a data warehouse based on PostgreSQL concepts, not MySQL-compatible."
    ]
   },
   {
    "q": "Which AWS service can run a managed PostgreSQL database that provides online transaction processing (OLTP)?",
    "o": [
     "Amazon DynamoDB",
     "Amazon Athena",
     "Amazon RDS (Relational Database Service)",
     "Amazon EMR"
    ],
    "a": 2,
    "e": "",
    "r": "Amazon RDS runs managed engines including PostgreSQL, and is designed for transactional (OLTP) workloads with automated backups, patching and Multi-AZ.",
    "w": [
     "A. DynamoDB is NoSQL.",
     "B. Athena queries data in S3 and is not for OLTP.",
     "D. EMR is for big data processing frameworks."
    ]
   },
   {
    "q": "Which AWS service is a key-value database that provides sub-millisecond latency on a large scale?",
    "o": [
     "Amazon DynamoDB",
     "Amazon Aurora",
     "Amazon DocumentDB (with MongoDB compatibility)",
     "Amazon Neptune"
    ],
    "a": 0,
    "e": "DynamoDB is a serverless NoSQL key-value database with single-digit millisecond latency.",
    "r": "DynamoDB is a key-value and document database with single-digit millisecond latency, and DynamoDB Accelerator (DAX) can reach microseconds, at any scale.",
    "w": [
     "B. Aurora is relational.",
     "C. DocumentDB is a document database for MongoDB workloads.",
     "D. Neptune is a graph database."
    ]
   },
   {
    "q": "A company wants to operate a data warehouse to analyze data without managing the data warehouse infrastructure. Which AWS service will meet this requirement?",
    "o": [
     "Amazon Aurora",
     "Amazon Redshift Serverless",
     "AWS Lambda",
     "Amazon RDS"
    ],
    "a": 1,
    "e": "Redshift is the managed data warehouse for analytics.",
    "r": "Amazon Redshift Serverless runs a data warehouse and automatically provisions and scales capacity, so the company analyzes data without managing infrastructure.",
    "w": [
     "A and D. Aurora and RDS are transactional databases.",
     "C. Lambda runs code and is not a data warehouse."
    ]
   },
   {
    "q": "A company wants an AWS service to provide product recommendations based on its customer data. Which AWS service will meet this requirement?",
    "o": [
     "Amazon Polly",
     "Amazon Personalize",
     "Amazon Comprehend",
     "Amazon Rekognition"
    ],
    "a": 1,
    "e": "Personalize provides recommendations from your data.",
    "r": "Amazon Personalize builds real-time personalized recommendations from your customer data using the same technology as Amazon.com, without needing ML expertise.",
    "w": [
     "A. Polly converts text to speech.",
     "C. Comprehend analyzes text.",
     "D. Rekognition analyzes images and video."
    ]
   },
   {
    "q": "A company is running a critical workload on an Amazon RDS DB instance. The company needs the DB instance to be highly available with a recovery time of less than 5 minutes. Which solution will meet these requirements?",
    "o": [
     "Createead replica of the DB instance",
     "Createemplate of the DB instance by using AWS CloudFormation",
     "Take frequent snapshots of the DB instance. Store the snapshots in Amazon S3.",
     "Modify the DB instance to be a Multi-AZ deployment"
    ],
    "a": 3,
    "e": "Multi-AZ keeps a standby in another AZ for automatic failover.",
    "r": "A Multi-AZ deployment keeps a synchronous standby in another Availability Zone and fails over automatically, usually within one to two minutes, so recovery time is under 5 minutes.",
    "w": [
     "A. A read replica helps with reads and needs manual promotion.",
     "B. CloudFormation only recreates infrastructure and takes longer.",
     "C. Restoring snapshots takes much longer and loses recent data."
    ]
   },
   {
    "q": "Which AWS service gives users the ability to build interactive business intelligence dashboards that include machine learning insights?",
    "o": [
     "Amazon Athena",
     "Amazon Kendra",
     "Amazon QuickSight",
     "Amazon Redshift"
    ],
    "a": 2,
    "e": "QuickSight builds interactive BI dashboards.",
    "r": "Amazon QuickSight is a business intelligence service for interactive dashboards, with built-in machine learning insights such as anomaly detection and forecasting.",
    "w": [
     "A. Athena runs SQL queries.",
     "B. Kendra is intelligent search.",
     "D. Redshift stores and queries data but is not a dashboard tool."
    ]
   },
   {
    "q": "A user needs to quickly deploy a nonrelational database on AWS. The user does not want to manage the underlying hardware or the database software. Which AWS service can be used to accomplish this?",
    "o": [
     "Amazon RDS",
     "Amazon DynamoDB",
     "Amazon Aurora",
     "Amazon Redshift"
    ],
    "a": 1,
    "e": "DynamoDB is a serverless NoSQL key-value database with single-digit millisecond latency.",
    "r": "DynamoDB is a nonrelational (NoSQL) database that is serverless, so the user manages neither hardware nor database software and can start in minutes.",
    "w": [
     "A, C and D. RDS and Aurora are relational, and Redshift is a data warehouse; none is a nonrelational database."
    ]
   },
   {
    "q": "A company needs to migrate a PostgreSQL database from on-premises to Amazon RDS. Which AWS service or tool should the company use to meet this requirement?",
    "o": [
     "Cloud Adoption Readiness Tool",
     "AWS Migration Hub",
     "AWS Database Migration Service (AWS DMS)",
     "AWS Application Migration Service"
    ],
    "a": 2,
    "e": "DMS migrates databases with minimal downtime.",
    "r": "AWS Database Migration Service (DMS) moves databases to AWS, including PostgreSQL to RDS, with minimal downtime.",
    "w": [
     "A. The Cloud Adoption Readiness Tool assesses readiness.",
     "B. Migration Hub tracks migrations.",
     "D. Application Migration Service moves servers, not databases."
    ]
   },
   {
    "q": "A company wants to migrate its PostgreSQL database to AWS. The company does not use the database frequently. Which AWS service or resource will meet these requirements with the LEAST management overhead?",
    "o": [
     "PostgreSQL on Amazon EC2",
     "Amazon RDS for PostgreSQL",
     "Amazon Aurora PostgreSQL-Compatible Edition",
     "Amazon Aurora Serverless"
    ],
    "a": 3,
    "e": "Aurora is a managed relational database compatible with MySQL and PostgreSQL.",
    "r": "Aurora Serverless scales capacity automatically and scales down when idle, so a rarely used PostgreSQL database needs the least management and low cost.",
    "w": [
     "A. PostgreSQL on EC2 requires full management.",
     "B. RDS needs an instance running all the time.",
     "C. Standard Aurora also runs provisioned instances continuously."
    ]
   }
  ]
 },
 {
  "t": "Networking & Content Delivery",
  "i": "🌐",
  "s": "VPC, connectivity and delivery",
  "q": [
   {
    "q": "Which of the following is an advantage that users experience when they move on-premises workloads to the AWS Cloud?",
    "o": [
     "Elimination of expenses for running and maintaining data centers",
     "Price discounts that are identical to discounts from hardware providers",
     "Distribution of all operational controls to AWS",
     "Elimination of operational expenses"
    ],
    "a": 0,
    "e": "A NAT gateway lets private subnets reach the internet without being reachable from it.",
    "r": "Moving to the cloud removes the cost of buying, running and maintaining data centers (real estate, power, cooling, hardware), replacing it with pay-as-you-go spending.",
    "w": [
     "B. Discounts are not identical to hardware vendors.",
     "C. Operational controls are shared, not all given to AWS.",
     "D. You still have operational expenses; they change in form."
    ]
   },
   {
    "q": "Which AWS service or feature allows a user to establish a dedicated network connection between a company’s on-premises data center and the AWS Cloud?",
    "o": [
     "AWS Direct Connect",
     "VPC peering",
     "AWS VPN",
     "Amazon Route 53"
    ],
    "a": 0,
    "e": "Direct Connect is a dedicated private network link to AWS.",
    "r": "AWS Direct Connect provides a dedicated private network connection between your data center and AWS, not over the public internet.",
    "w": [
     "B. VPC peering links two VPCs.",
     "C. A VPN is an encrypted connection over the internet, not dedicated.",
     "D. Route 53 is DNS."
    ]
   },
   {
    "q": "What is the purpose of having an internet gateway within a VPC?",
    "o": [
     "To createPN connection to the VPC",
     "To allow communication between the VPC and the internet",
     "To impose bandwidth constraints on internet tra",
     "To load balance tra"
    ],
    "a": 1,
    "e": "",
    "r": "An internet gateway allows communication between resources in the VPC and the internet.",
    "w": [
     "A. A VPN connection uses a virtual private gateway.",
     "C and D. An internet gateway does not limit bandwidth or balance load; a load balancer does that."
    ]
   },
   {
    "q": "A company is building an application that needs to deliver images and videos globally with minimal latency. Which approach can the company use to accomplish this in a cost effective manner?",
    "o": [
     "Deliver the content through Amazon CloudFront",
     "Store the content on Amazon S3 and enable S3 cross-region replication",
     "ImplementPN across multiple AWS Regions",
     "Deliver the content through AWS PrivateLink"
    ],
    "a": 0,
    "e": "CloudFront caches content at edge locations for low latency.",
    "r": "Amazon CloudFront is a content delivery network that caches images and videos at edge locations around the world, giving low latency at reasonable cost.",
    "w": [
     "B. Cross-region replication copies data but does not serve users from nearby edge caches.",
     "C. Running a VPN does not speed content delivery.",
     "D. PrivateLink is private connectivity."
    ]
   },
   {
    "q": "Which AWS service helps deliver highly available applications with fast failover for multi-Region and Multi-AZ architectures?",
    "o": [
     "AWS WAF",
     "AWS Global Accelerator",
     "AWS Shield",
     "AWS Direct Connect"
    ],
    "a": 1,
    "e": "Global Accelerator routes traffic over the AWS network to healthy endpoints across Regions.",
    "r": "AWS Global Accelerator uses the AWS global network and static IP addresses to route users to healthy endpoints, with fast failover across Regions and Availability Zones.",
    "w": [
     "A. WAF filters requests.",
     "C. Shield protects against DDoS.",
     "D. Direct Connect is a dedicated link."
    ]
   },
   {
    "q": "Which AWS service enables companies to deploy an application close to end users?",
    "o": [
     "Amazon CloudFront",
     "AWS Auto Scaling",
     "AWS AppSync",
     "Amazon Route 53"
    ],
    "a": 0,
    "e": "CloudFront caches content at edge locations for low latency.",
    "r": "Amazon CloudFront deploys content at edge locations close to end users to cut latency.",
    "w": [
     "B. Auto Scaling adjusts capacity.",
     "C. AppSync is GraphQL.",
     "D. Route 53 is DNS, though it can route by latency."
    ]
   },
   {
    "q": "A company has an Amazon EC2 instance in a private subnet. The company wants to initiate a connection to the internet to pull operating system Which AWS managed service allows this?",
    "o": [
     "VPC endpoint",
     "NAT gateway",
     "Amazon PrivateLink",
     "VPC peering"
    ],
    "a": 1,
    "e": "A NAT gateway lets private subnets reach the internet without being reachable from it.",
    "r": "A NAT gateway lets instances in a private subnet start outbound connections (for updates) while blocking inbound connections from the internet.",
    "w": [
     "A and C. VPC endpoints and PrivateLink connect to AWS services privately, not the general internet.",
     "D. VPC peering connects VPCs."
    ]
   },
   {
    "q": "A company wants to create a globally accessible ecommerce platform for its customers. The company wants to use a highly available and scalable DNS web service to connect users to the platform. Which AWS service will meet these requirements?",
    "o": [
     "Amazon EC2",
     "Amazon VPC",
     "Amazon Route 53",
     "Amazon RDS"
    ],
    "a": 2,
    "e": "Route 53 is scalable DNS with routing policies and health checks.",
    "r": "Amazon Route 53 is a highly available, scalable DNS service that routes users to applications.",
    "w": [
     "A. EC2 is compute.",
     "B. VPC is a network.",
     "D. RDS is a database."
    ]
   }
  ]
 },
 {
  "t": "Management & Governance",
  "i": "🏛️",
  "s": "Auditing, automation and accounts",
  "q": [
   {
    "q": "A company wants to manage deployed IT services and govern its infrastructure as code (IaC) templates. Which AWS service will meet this requirement?",
    "o": [
     "AWS Resource Explorer",
     "AWS Service Catalog",
     "AWS Organizations",
     "AWS Systems Manager"
    ],
    "a": 1,
    "e": "Service Catalog lets you govern approved IaC products that teams can deploy.",
    "r": "AWS Service Catalog lets organizations create and manage catalogs of approved IT services and infrastructure-as-code templates (for example CloudFormation), so users deploy only governed products.",
    "w": [
     "A. Resource Explorer searches your resources.",
     "C. Organizations manages accounts and policies.",
     "D. Systems Manager operates and automates tasks on resources."
    ]
   },
   {
    "q": "Which AWS service provides the ability to manage infrastructure as code?",
    "o": [
     "AWS CodePipeline",
     "AWS CodeDeploy",
     "AWS Direct Connect",
     "AWS CloudFormation"
    ],
    "a": 3,
    "e": "CloudFormation provisions resources from templates (infrastructure as code).",
    "r": "AWS CloudFormation provisions and manages infrastructure from templates, so the whole environment is described as code and created consistently.",
    "w": [
     "A. CodePipeline automates release pipelines.",
     "B. CodeDeploy deploys application code.",
     "C. Direct Connect is a dedicated network connection."
    ]
   },
   {
    "q": "Which service enables customers to audit API calls in their AWS accounts?",
    "o": [
     "AWS CloudTrail",
     "AWS Trusted Advisor",
     "Amazon Inspector",
     "AWS X-Ray"
    ],
    "a": 0,
    "e": "CloudTrail records API activity: who did what, when and from where.",
    "r": "AWS CloudTrail records API calls and account activity in your AWS account, including who made the call, when and from where, so you can audit it.",
    "w": [
     "B. Trusted Advisor gives recommendations on cost, performance and security.",
     "C. Inspector scans for vulnerabilities.",
     "D. X-Ray traces requests in applications."
    ]
   },
   {
    "q": "Which AWS service can identify when an Amazon EC2 instance was terminated?",
    "o": [
     "AWS Identity and Access Management (IAM)",
     "AWS CloudTrail",
     "AWS Compute Optimizer",
     "Amazon EventBridge"
    ],
    "a": 1,
    "e": "CloudTrail records API activity: who did what, when and from where.",
    "r": "CloudTrail logs the TerminateInstances API call with the identity, time and source, so you can find when and by whom an instance was terminated.",
    "w": [
     "A. IAM manages identities and permissions but does not keep that history.",
     "C. Compute Optimizer recommends sizes.",
     "D. EventBridge routes events and is not the audit record."
    ]
   },
   {
    "q": "Which AWS service provides command line access to AWS tools and resources directly from a web browser?",
    "o": [
     "AWS CloudHSM",
     "AWS CloudShell",
     "Amazon WorkSpaces",
     "AWS Cloud Map"
    ],
    "a": 1,
    "e": "CloudShell is a browser-based, pre-authenticated shell in the console.",
    "r": "AWS CloudShell is a browser-based shell, pre-authenticated with your console credentials and with the AWS CLI installed, available from the console.",
    "w": [
     "A. CloudHSM provides hardware key storage.",
     "C. WorkSpaces provides virtual desktops.",
     "D. Cloud Map is a service discovery tool."
    ]
   },
   {
    "q": "A company wants to manage its AWS Cloud resources through a web interface. Which AWS service will meet this requirement?",
    "o": [
     "AWS Management Console",
     "AWS CLI",
     "AWS SDK",
     "AWS Cloud9"
    ],
    "a": 0,
    "e": "The Management Console is the web interface for AWS.",
    "r": "The AWS Management Console is the web-based interface for managing AWS resources.",
    "w": [
     "B. The CLI is a command-line tool.",
     "C. The SDK is used in code.",
     "D. Cloud9 is a cloud-based IDE."
    ]
   },
   {
    "q": "A developer needs to maintain a development environment infrastructure and a production environment infrastructure in a repeatable fashion. Which AWS service should the developer use to meet these requirements?",
    "o": [
     "AWS Ground Station",
     "AWS Shield",
     "AWS IoT Device Defender",
     "AWS CloudFormation"
    ],
    "a": 3,
    "e": "CloudFormation provisions resources from templates (infrastructure as code).",
    "r": "AWS CloudFormation recreates identical environments from the same template, so development and production infrastructure stay consistent and repeatable.",
    "w": [
     "A. Ground Station is for satellite data.",
     "B. Shield protects against DDoS.",
     "C. IoT Device Defender audits IoT device security."
    ]
   },
   {
    "q": "Which tool should a developer use to integrate AWS service features directly into an application?",
    "o": [
     "AWS Software Development Kit",
     "AWS CodeDeploy",
     "AWS Lambda",
     "AWS Batch"
    ],
    "a": 0,
    "e": "SDKs let applications call AWS services from code.",
    "r": "The AWS Software Development Kit (SDK) lets a developer call AWS services directly from application code in languages such as Python, Java and JavaScript.",
    "w": [
     "B. CodeDeploy deploys code.",
     "C. Lambda runs code.",
     "D. Batch runs batch jobs."
    ]
   },
   {
    "q": "A company needs to track the activity in its AWS accounts, and needs to know when an API call is made against its AWS resources. Which AWS tool or service can be used to meet these requirements?",
    "o": [
     "Amazon CloudWatch",
     "Amazon Inspector",
     "AWS CloudTrail",
     "AWS IAM"
    ],
    "a": 2,
    "e": "CloudTrail records API activity: who did what, when and from where.",
    "r": "AWS CloudTrail tracks user activity and API usage, recording when an API call is made against your resources.",
    "w": [
     "A. CloudWatch monitors performance metrics and logs.",
     "B. Inspector scans for vulnerabilities.",
     "D. IAM manages access."
    ]
   },
   {
    "q": "Which AWS service or feature allows users to create new AWS accounts, group multiple accounts to organize work?",
    "o": [
     "AWS Identity and Access Management (IAM)",
     "AWS Trusted Advisor",
     "AWS CloudFormation",
     "AWS Organizations"
    ],
    "a": 3,
    "e": "Organizations manages many accounts, SCPs and consolidated billing at no extra charge.",
    "r": "AWS Organizations lets you create new accounts, group accounts into organizational units and apply policies and consolidated billing.",
    "w": [
     "A. IAM works inside one account.",
     "B. Trusted Advisor gives recommendations.",
     "C. CloudFormation provisions resources."
    ]
   },
   {
    "q": "Which AWS service should users use to learn about AWS service availability and operations?",
    "o": [
     "Amazon EventBridge",
     "AWS Service Catalog",
     "AWS Control Tower",
     "AWS Health Dashboard"
    ],
    "a": 3,
    "e": "The Health Dashboard shows AWS service health and events affecting your account.",
    "r": "The AWS Health Dashboard shows the availability and operational status of AWS services and events affecting your resources.",
    "w": [
     "A. EventBridge routes events between services.",
     "B. Service Catalog manages approved products.",
     "C. Control Tower sets up multi-account environments."
    ]
   },
   {
    "q": "A company wants to create templates that the company can reuse to deploy multiple AWS resources. Which AWS service or feature can the company use to meet this requirement?",
    "o": [
     "AWS Marketplace",
     "Amazon Machine Image (AMI)",
     "AWS CloudFormation",
     "AWS OpsWorks"
    ],
    "a": 2,
    "e": "CloudFormation provisions resources from templates (infrastructure as code).",
    "r": "CloudFormation templates are reusable files that describe resources, so the same template can deploy many identical sets of resources.",
    "w": [
     "A. Marketplace sells software.",
     "B. An AMI is an instance image, not a multi-resource template.",
     "D. OpsWorks uses Chef or Puppet for configuration."
    ]
   },
   {
    "q": "Which AWS service or feature is a browser-based, pre-authenticated service that can be launched directly from the AWS Management Console?",
    "o": [
     "AWS API",
     "AWS Lightsail",
     "AWS Cloud9",
     "AWS CloudShell"
    ],
    "a": 3,
    "e": "CloudShell is a browser-based, pre-authenticated shell in the console.",
    "r": "AWS CloudShell is a browser-based, pre-authenticated shell that launches directly from the console.",
    "w": [
     "A. The AWS API is not a launched tool.",
     "B. Lightsail is a simple hosting service.",
     "C. Cloud9 is a cloud IDE that is not pre-authenticated in the same way."
    ]
   },
   {
    "q": "Which AWS service allows users to model and provision AWS resources using common programming languages?",
    "o": [
     "AWS CloudFormation",
     "AWS CodePipeline",
     "AWS Cloud Development Kit (AWS CDK)",
     "AWS Systems Manager"
    ],
    "a": 2,
    "e": "The AWS CDK defines infrastructure using programming languages.",
    "r": "The AWS Cloud Development Kit (CDK) lets you model and provision resources using familiar programming languages such as Python, TypeScript and Java, and generates CloudFormation.",
    "w": [
     "A. CloudFormation uses JSON or YAML templates, not general programming languages.",
     "B. CodePipeline automates delivery.",
     "D. Systems Manager operates resources."
    ]
   },
   {
    "q": "What can a user accomplish using AWS CloudTrail?",
    "o": [
     "Generate an IAM user credentials report",
     "Record API calls made to AWS services",
     "Assess the compliance of AWS resource con",
     "Ensure that Amazon EC2 instances are patched with the latest security updates"
    ],
    "a": 1,
    "e": "",
    "r": "CloudTrail records API calls made to AWS services, which supports auditing and security investigations.",
    "w": [
     "A. The IAM credential report lists credentials.",
     "C. AWS Config assesses resource configuration compliance.",
     "D. Systems Manager Patch Manager keeps instances patched."
    ]
   },
   {
    "q": "A company wants to improve its security posture by reviewing user activity through API calls. Which AWS service will meet this requirement?",
    "o": [
     "AWS WAF",
     "Amazon Detective",
     "Amazon CloudWatch",
     "AWS CloudTrail"
    ],
    "a": 3,
    "e": "CloudTrail records API activity: who did what, when and from where.",
    "r": "CloudTrail provides the history of API calls, so the company can review user activity to improve its security posture.",
    "w": [
     "A. WAF filters web traffic.",
     "B. Detective investigates findings but relies on logs like CloudTrail.",
     "C. CloudWatch monitors metrics and logs, not API activity history."
    ]
   },
   {
    "q": "A company wants to launch multiple workloads on AWS. Each workload is related to a different business unit. The company wants to separate and track costs for each business unit. Which solution will meet these requirements with the LEAST operational overhead?",
    "o": [
     "Use AWS Organizations and create one account for each business unit",
     "Usepreadsheet to control the owners and cost of each resource",
     "Use an Amazon DynamoDB table to record costs for each business unit",
     "Use the AWS Billing console to assign owners to resources and track costs"
    ],
    "a": 0,
    "e": "Organizations manages many accounts, SCPs and consolidated billing at no extra charge.",
    "r": "Using AWS Organizations with one account per business unit separates workloads and gives costs per account automatically, with consolidated billing. It needs the least operational overhead.",
    "w": [
     "B and C. A spreadsheet or DynamoDB table must be updated by hand.",
     "D. The Billing console cannot assign owners to resources by itself."
    ]
   }
  ]
 },
 {
  "t": "Migration, Integration & Marketplace",
  "i": "🔗",
  "s": "Moving to AWS and working with partners",
  "q": [
   {
    "q": "A global company wants to migrate its third-party applications to the AWS Cloud. The company wants help from a global team of experts to complete the migration faster and more reliably in accordance with AWS internal best practices. Which AWS service or resource will meet these requirements?",
    "o": [
     "AWS Support",
     "AWS Professional Services",
     "AWS Launch Wizard",
     "AWS Managed Services (AMS)"
    ],
    "a": 1,
    "e": "AWS Professional Services are AWS experts who help with migrations and projects.",
    "r": "AWS Professional Services is a global team of AWS experts who work with customers to complete migrations faster and more reliably following AWS best practices.",
    "w": [
     "A. AWS Support gives technical assistance, not migration projects.",
     "C. Launch Wizard guides deployment of specific applications.",
     "D. AWS Managed Services operates infrastructure after migration."
    ]
   },
   {
    "q": "A company plans to migrate to the AWS Cloud. The company is gathering information about its on-premises infrastructure and requires information such as the hostname, IP address, and MAC address. Which AWS service will meet these requirements?",
    "o": [
     "AWS DataSync",
     "AWS Application Migration Service",
     "AWS Application Discovery Service",
     "AWS Database Migration Service (AWS DMS)"
    ],
    "a": 2,
    "e": "Application Discovery Service collects on-premises server details before migration.",
    "r": "AWS Application Discovery Service collects information about on-premises servers, such as hostnames, IP addresses, MAC addresses and utilization, to plan a migration.",
    "w": [
     "A. DataSync transfers data.",
     "B. Application Migration Service rehosts servers to AWS.",
     "D. DMS migrates databases."
    ]
   },
   {
    "q": "A company needs to use third-party software for its workload on AWS. Which AWS service or feature can the company use to purchase the software?",
    "o": [
     "AWS Resource Access Manager",
     "AWS Managed Services",
     "AWS License Manager",
     "AWS Marketplace"
    ],
    "a": 3,
    "e": "AWS Marketplace is the catalog for buying third-party software.",
    "r": "AWS Marketplace is a catalog where you can find, buy and deploy third-party software on AWS, with billing through your AWS account.",
    "w": [
     "A. Resource Access Manager shares resources between accounts.",
     "B. AWS Managed Services operates infrastructure.",
     "C. License Manager tracks licenses you already own."
    ]
   },
   {
    "q": "A company needs to engage third-party consultants to help maintain and support its AWS environment and the company’s business needs. Which AWS service or resource will meet these requirements?",
    "o": [
     "AWS Support",
     "AWS Organizations",
     "AWS Service Catalog",
     "AWS Partner Network (APN)"
    ],
    "a": 3,
    "e": "The AWS Partner Network connects you with consulting and technology partners.",
    "r": "The AWS Partner Network (APN) connects customers with consulting and technology partners who can maintain and support an AWS environment.",
    "w": [
     "A. AWS Support is provided by AWS itself, not third-party consultants.",
     "B. Organizations manages accounts.",
     "C. Service Catalog manages approved products."
    ]
   },
   {
    "q": "A company wants to migrate to AWS and use the same security software it uses on premises. The security software vendor offers its security software as a service on AWS. Where can the company purchase the security solution?",
    "o": [
     "AWS Partner Solutions Finder",
     "AWS Support Center",
     "AWS Management Console",
     "AWS Marketplace"
    ],
    "a": 3,
    "e": "AWS Marketplace is the catalog for buying third-party software.",
    "r": "AWS Marketplace is where you can purchase third-party solutions, including security software offered as a service, and deploy them on AWS.",
    "w": [
     "A. Partner Solutions Finder helps find partners, not buy software.",
     "B. Support Center is for support cases.",
     "C. The Management Console is the interface for AWS services."
    ]
   }
  ]
 }
];
